/**
 * The shape of a screen manifest, and the rule that makes the renderer honest: a manifest may ask
 * for something this version does not know how to draw, and the only safe answer is to say so.
 * A text box standing in for a field of an unknown type silently changes what a person can say.
 */
import { LINES_ONLY, isLinesField, keptLines, lineChoiceKey, lineColumns, linesDeclarationProblems, linesProblem, sentLines, startLines } from "./lines.js";
import { paperProblems } from "./paper.js";
import { inspectTimeline } from "./timeline-model.js";
export const COLUMN_TYPES = ['text', 'number', 'money', 'datetime', 'date', 'time', 'bytes', 'count', 'colour', 'boolean', 'list', 'namespaces', 'percent'];
/** Column types a `sum` statistic may add. */
export const SUMMABLE_COLUMN_TYPES = ['number', 'money', 'count', 'bytes'];
export const FIELD_TYPES = ['text', 'number', 'integer', 'boolean', 'select', 'textarea', 'multiselect', 'file', 'audio', 'date', 'time', 'keyvalue', 'secret', 'lines'];
/**
 * A secret is written and never read back (issue 107): its field starts empty whatever the row holds, an
 * empty one is not sent (so saving the form keeps the secret there is), and the assistant never
 * fills one (`isSecretField`). What the row may say of it — its last four — is `current_from`.
 */
export const isSecret = (field) => field.type === 'secret';
export const OPTIONS_SOURCES = ['reason_codes'];
export const OPTIONS_SOURCE_PATHS = { reason_codes: '/reason-codes' };
export const LAYOUTS = ['table', 'cards', 'calendar', 'timeline'];
export const DISPLAY_KINDS = ['badge', 'badges', 'person', 'mono', 'relative', 'progress', 'phone', 'moment', 'country', 'image', 'conversation', 'event', 'email'];
export const PHONE_CHANNELS = ['whatsapp'];
export const TONES = ['green', 'amber', 'red', 'blue', 'violet', 'neutral'];
export const FILTER_OPERATORS = ['contains', 'starts', 'eq', 'in', 'gt', 'lt', 'between', 'on', 'before', 'after', 'is'];
export const OPERATORS_FOR = {
    text: ['contains', 'starts', 'eq', 'in'],
    number: ['eq', 'gt', 'lt', 'between'],
    money: ['eq', 'gt', 'lt', 'between'],
    count: ['eq', 'gt', 'lt', 'between'],
    bytes: ['eq', 'gt', 'lt', 'between'],
    date: ['on', 'before', 'after', 'between'],
    datetime: ['on', 'before', 'after', 'between'],
    time: [],
    boolean: ['is'],
    list: ['in'],
    colour: [],
    namespaces: [],
    percent: ['eq', 'gt', 'lt', 'between'],
};
/** A `list` has no order, a `colour` is not a quantity, and namespaces are a set. */
export const SORTABLE_COLUMN_TYPES = ['text', 'number', 'money', 'datetime', 'date', 'time', 'bytes', 'count', 'boolean', 'percent'];
export const PAGE_SIZES = [10, 25, 50, 100];
export const EXPORT_FORMATS = ['csv', 'xlsx', 'docx', 'pdf'];
export const AGGREGATES = ['count', 'percent', 'sum', 'oldest'];
export const ICON_NAMES = [
    'circle', 'user', 'users', 'globe', 'star', 'lock', 'pencil', 'plus', 'copy',
    'trash', 'check', 'clock', 'shield', 'tag', 'file', 'settings', 'search', 'calendar',
    'clipboard', 'x', 'arrow_right', 'alert', 'history', 'eye',
    'banknote', 'refresh', 'scale', 'key', 'book', 'wallet', 'id_card', 'undo', 'arrow_left', 'landmark', 'message',
];
/** A `file` field's value: the bytes, or — `store: "media"` — the reference to them. */
export function isMediaRef(v) {
    return Boolean(v) && typeof v === 'object' && !Array.isArray(v) && typeof v.media_id === 'string' && v.media_id !== '';
}
export const FIELD_UNITS = ['sar', 'percent', 'day', 'month', 'year', 'minute', 'metre'];
export const FIELD_FORMATS = {
    code: { pattern: /^[a-z0-9][a-z0-9_.@-]*$/i, inputMode: 'text' },
    ltr: { inputMode: 'text' },
    email: { pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, inputMode: 'email', placeholder: 'name@example.com' },
    phone: { pattern: /^\+?[0-9 ]{9,17}$/, inputMode: 'tel', placeholder: '+966 5x xxx xxxx' },
    vat: { pattern: /^3[0-9]{13}3$/, inputMode: 'numeric', maxLength: 15, placeholder: '3xxxxxxxxxxxxx3' },
    cr: { pattern: /^[0-9]{10}$/, inputMode: 'numeric', maxLength: 10, placeholder: '0000000000' },
    // A person's number under the SA scheme, of either kind: a national ID (1…) or an iqama (2…). A field that can
    // only hold one declares it — `iqama` below; narrowing this one would refuse real people.
    national_id: { pattern: /^[12][0-9]{9}$/, inputMode: 'numeric', maxLength: 10, placeholder: '1xxxxxxxxx' },
    iqama: { pattern: /^2[0-9]{9}$/, inputMode: 'numeric', maxLength: 10, placeholder: '2xxxxxxxxx' },
    // An IBAN under the SA country code: SA and 22 digits, 24 characters, written without spaces.
    iban: { pattern: /^SA[0-9]{22}$/, inputMode: 'text', maxLength: 24, placeholder: 'SA0000000000000000000000' },
    postcode: { pattern: /^[0-9]{5}$/, inputMode: 'numeric', maxLength: 5, placeholder: '00000' },
    building: { pattern: /^[0-9]{4}$/, inputMode: 'numeric', maxLength: 4, placeholder: '0000' },
};
/** The catalogue key of the refusal a value earns against its field's format, or null when it fits (or is empty). */
export function formatProblem(field, value) {
    if (!field.format || value === undefined || value === null || value === '')
        return null;
    const rule = FIELD_FORMATS[field.format];
    if (!rule?.pattern)
        return null;
    return rule.pattern.test(westernDigits(String(value)).trim()) ? null : `field.format.${field.format}`;
}
/**
 * A text against its declared `pattern` and length. The same test `packages/fields` makes on the
 * server (`checkValue`): trimmed, the whole value, Unicode — so a value this passes is one the
 * server's copy of the rule passes too. An empty value is the required check's business, not this.
 * A pattern that is not a regular expression refuses nothing here: the schema build refuses it.
 */
export function ruleProblem(field, value) {
    if (value === undefined || value === null || value === '' || (typeof value !== 'string' && typeof value !== 'number'))
        return null;
    const text = westernDigits(String(value)).trim();
    if (field.min_length !== undefined && text.length < field.min_length)
        return { key: 'field.length.min', vars: { min: field.min_length } };
    if (field.max_length !== undefined && text.length > field.max_length)
        return { key: 'field.length.max', vars: { max: field.max_length } };
    if (field.pattern) {
        let re;
        try {
            re = new RegExp(`^(?:${field.pattern})$`, 'u');
        }
        catch {
            return null;
        }
        if (!re.test(text))
            return { key: field.pattern_message_key ?? 'field.pattern' };
    }
    return null;
}
/**
 * A number outside its declared `min`/`max`, said rather than moved. The number box used to put an
 * out-of-range value back inside its bounds on leaving the field, which saved something the person
 * never typed; now the value stays as typed and this says what the bounds are.
 */
export function rangeProblem(field, value) {
    if (field.type !== 'number' && field.type !== 'integer')
        return null;
    if (typeof value !== 'number' || !Number.isFinite(value))
        return null;
    if (field.type === 'integer' && !Number.isInteger(value))
        return { key: 'field.wholeNumber' };
    const { min, max } = field;
    if ((min !== undefined && value < min) || (max !== undefined && value > max)) {
        if (min !== undefined && max !== undefined)
            return { key: 'field.range.between', vars: { min, max } };
        return min !== undefined ? { key: 'field.range.min', vars: { min } } : { key: 'field.range.max', vars: { max: max } };
    }
    return null;
}
/** Everything a value may be refused for before it is sent, first found: its format, its rules, its range. */
export function fieldProblem(field, value) {
    // Lines are checked cell by cell (lines.ts); under the grid the form says the count or «correct the marked cells».
    if (isLinesField(field))
        return linesProblem(field, value);
    const format = formatProblem(field, value);
    if (format)
        return { key: format };
    return ruleProblem(field, value) ?? rangeProblem(field, value);
}
/** Arabic-Indic and Persian digits as Western ones: a phone keyboard in Arabic types ٣٠٠…, and the number is the same. */
export function westernDigits(text) {
    return text.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x660)).replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x6f0));
}
/** The fields the form shows now: those whose `when` holds against the values chosen so far. */
export function visibleFields(action, values) {
    return (action.fields ?? []).filter((f) => holds(values, f.when));
}
/** Today, as the ISO day a date field holds — the one symbolic default a form may declare. */
export function today(now = new Date()) {
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
    return local.toISOString().slice(0, 10);
}
/** The answer's word on one condition: true, false, or null when the answer does not say. */
export function availableIn(spec, answer) {
    const row = Array.isArray(answer) ? (answer.length ? answer[0] : {}) : answer;
    if (!row || typeof row !== 'object' || Array.isArray(row))
        return null;
    // An empty list holds nothing; a row that does not carry the property says nothing.
    if (Array.isArray(answer) && !answer.length)
        return false;
    if (!(spec.property in row))
        return null;
    const value = row[spec.property];
    if (Array.isArray(value))
        return value.length > 0;
    return Boolean(value);
}
/** The routes a screen's actions ask before they are offered (`available`), each once. */
export function availabilityPaths(actions) {
    const paths = [];
    for (const a of actions ?? []) {
        const path = a.available?.path;
        if (path && askable(path) && !paths.includes(path))
            paths.push(path);
    }
    return paths;
}
/** A path a condition may be asked at: on the module's own unit, with nothing to fill in. */
function askable(path) {
    return SOURCE_PATH.test(path) && !/[{}]/.test(path);
}
/**
 * The actions as they may be offered now, given what each `available` route answered. `answers`
 * holds a path once it has answered — its body, or `null` when it could not be read. A path not yet
 * there is pending: a `hide` action is not drawn and a `disable` one is drawn disabled, so nothing
 * opens before it is known. A route that could not be read, or an answer that does not carry the
 * property, leaves the action as declared — the condition spares a person a form the server would
 * refuse, and the server still judges the form when it is sent. Unchanged actions keep their identity.
 */
export function resolveAvailable(actions, answers) {
    const out = [];
    for (const action of actions) {
        const spec = action.available;
        // A path that is never asked could never answer: the action stays as declared, not held for ever.
        if (!spec || typeof spec.path !== 'string' || !askable(spec.path)) {
            out.push(action);
            continue;
        }
        const answered = Object.prototype.hasOwnProperty.call(answers, spec.path);
        const verdict = !answered ? 'pending' : answers[spec.path] === null ? null : availableIn(spec, answers[spec.path]) === false ? 'no' : null;
        if (verdict === null) {
            out.push(spec.held ? { ...action, available: withoutHeld(spec) } : action);
            continue;
        }
        if (spec.otherwise === 'hide')
            continue;
        out.push(spec.held === verdict ? action : { ...action, available: { ...spec, held: verdict } });
    }
    return out;
}
function withoutHeld(spec) {
    const { held: _held, ...rest } = spec;
    return rest;
}
/** A screen's manifest with its actions resolved against their `available` answers (resolveAvailable). */
export function resolveManifestAvailable(manifest, answers) {
    if (!manifest.actions?.some((a) => a.available))
        return manifest;
    const actions = resolveAvailable(manifest.actions, answers);
    return actions.length === manifest.actions.length && actions.every((a, i) => a === manifest.actions[i]) ? manifest : { ...manifest, actions };
}
/** Whether a condition keeps this action from being run now (resolveAvailable). */
export function heldBack(action) {
    return action.available?.held !== undefined;
}
export const ACTION_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];
/** The most fields a bulk confirm asks: a one-column dialog's (F-1), beside the counts, never scrolling. */
export const BULK_FORM_FIELDS = 5;
/**
 * The most it asks when it also previews each row: the preview's table grows with the selection,
 * so the form beside it keeps to three (Fable, 2026-10-27). The table scrolls inside its own box.
 */
export const BULK_PREVIEW_FORM_FIELDS = 3;
/** How many fields a bulk confirm may ask for this action: fewer beside a preview. */
export function bulkFieldCap(action) {
    return typeof action.bulk === 'object' && action.bulk !== null && action.bulk.preview === true ? BULK_PREVIEW_FORM_FIELDS : BULK_FORM_FIELDS;
}
/** The most facts a form's preview draws: a strip that stays one line in a dialog that never scrolls. */
export const FORM_PREVIEW_COLUMNS = 4;
/** Whether a form asks the server what it would come to while it is filled in, and what it draws of the answer. */
export function formPreview(action) {
    if (!action.preview || action.component || !(action.fields?.length))
        return null;
    return action.preview.columns?.length ? action.preview : null;
}
/**
 * Whether a form is ready to be previewed: every required field holds a value, and none the form's
 * own rules refuse (the shape, the declared pattern and range, an end before its start). Half a form
 * is not asked about — the answer would be about a request nobody is going to send.
 */
export function previewReady(action, values) {
    if (missingRequired(action, values).length)
        return false;
    return visibleFields(action, values).every((f) => fieldProblem(f, values[f.key]) === null && orderProblem(f, values) === null);
}
export const THEN_VALUES = ['reload', 'reveal', 'download', 'open', 'link'];
/**
 * The address an answer carries for `then: link`: its `url` (or `href`), and only an http or https
 * one — an answer is not a place this code can vouch for, and `javascript:` in a new tab is a script
 * run under the viewer's name. Null when there is none: the caller says so rather than doing nothing.
 */
export function linkOfAnswer(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data))
        return null;
    const raw = data['url'] ?? data['href'];
    if (typeof raw !== 'string' || !raw)
        return null;
    try {
        const url = new URL(raw);
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
    }
    catch {
        return null;
    }
}
/** Open an address an answer carried in a new tab, with no way back to this page from it. */
export function goTo(url) {
    if (typeof document === 'undefined')
        return;
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
}
/** Whether this action may run over a selection. */
export function isBulk(action) {
    return action.bulk === true || (typeof action.bulk === 'object' && action.bulk !== null);
}
/** Whether a bulk action opens its page with the selection (`bulk.open`) rather than running per row. */
export function bulkOpens(action) {
    return typeof action.bulk === 'object' && action.bulk !== null && action.bulk.open === true && Boolean(action.screen);
}
/**
 * The address query a bulk `open` hands its page: `ids=` the selected rows' keys, comma-joined, in
 * the order chosen, each once — the rows the action's `when` holds for and that carry a key. Empty
 * when none does, and the page is not opened.
 */
export function selectionQuery(action, rows) {
    const key = action.row_key ?? 'id';
    const ids = [...new Set(rows.filter((r) => holds(r, action.when)).map((r) => r[key])
            .filter((v) => (typeof v === 'string' && v !== '') || typeof v === 'number').map(String))];
    return ids.length ? `ids=${ids.map(encodeURIComponent).join(',')}` : '';
}
/** Whether a bulk action asks the server for each row's effect before the confirm. */
export function bulkPreview(action) {
    return typeof action.bulk === 'object' && action.bulk !== null && action.bulk.preview ? action.bulk : null;
}
/** Whether a bulk action asks its fields once in the confirm and sends the answers with every row. */
export function bulkAsks(action) {
    return typeof action.bulk === 'object' && action.bulk !== null && action.bulk.fields === true && (action.fields?.length ?? 0) > 0;
}
/**
 * The body one row of a bulk run carries: the answers asked once (the same for every row), and the
 * version that row was read at — so a route that checks it refuses a row somebody changed while the
 * confirm stood open, exactly as it would the single action (#29).
 */
export function bulkBody(action, values, row) {
    const body = actionBody(action, bulkAsks(action) ? values : {}, row);
    return Object.keys(body).length ? body : undefined;
}
/**
 * Where a preview is asked: the action's own path with `/preview` after it. The bulk is the row
 * action run once per row, and its preview is that too — no second route to keep in step; a form's
 * preview (`Action.preview`) is the same path for the one request the form would send.
 */
export function previewPathFor(action, row) {
    const path = pathFor(action, row);
    return path ? `${path.replace(/\/$/, '')}/preview` : null;
}
/** The most lines a form preview's advice draws: a dialog that never scrolls has room for few. */
export const MAX_ADVICE = 3;
/** Whether this filter is drawn as a select in the toolbar. */
export function isQuick(filter) {
    if (!isApplicable(filter))
        return false;
    return filter.quick ?? !filter.operators?.length;
}
/** Whether this filter narrows anything — `applies: false` is a dimension the screen says it has not got. */
export function isApplicable(filter) {
    return filter.applies !== false;
}
export const EMPHASIS_TONES = ['red', 'amber'];
/** The marks of a column that hold for this row, in the order declared. */
export function marksOf(column, row) {
    if (!row)
        return [];
    return (column.marks ?? []).filter((m) => m && typeof m.label === 'string' && m.label && holds(row, m.when));
}
/** The first emphasis rule that holds for this row, or null. */
export function emphasisOf(manifest, row) {
    for (const rule of manifest.rows?.emphasis ?? [])
        if (rule && EMPHASIS_TONES.includes(rule.tone) && holds(row, rule.when))
            return rule;
    return null;
}
/** The calendar's views this version draws; a declared `week` is left out until it does. */
export const CALENDAR_VIEWS = ['month', 'agenda'];
export const REPORT_PARAM_TYPES = ['date', 'date_range', 'period', 'select', 'boolean'];
/** What a report's columns may be. The words of each line are its `label`, not a column. */
export const REPORT_COLUMN_TYPES = ['money', 'number', 'percent', 'text'];
export const REPORT_CHART_DRAWS = ['bars', 'trend'];
/** `{module}.{entity}:{id}` read as the record it names; null for anything else. */
export function parseRefToken(value) {
    if (typeof value !== 'string')
        return null;
    const m = /^([a-z][a-z0-9_]*\.[a-z][a-z0-9_]*):(\S+)$/.exec(value);
    return m ? { entity: m[1], id: m[2] } : null;
}
/**
 * The record a column names on this row: its own `ref` when the row holds that id, otherwise the
 * first of `also` whose id the row holds; for a `token` reference, what the token says. Null when
 * the row names nothing. `idColumn` is the other column the id was read from, when it was.
 */
export function refOf(column, row, value) {
    const ref = column.ref;
    if (!ref)
        return null;
    if (ref.token !== undefined) {
        const own = ref.token === column.key;
        const found = parseRefToken(own && value !== undefined ? value : row?.[ref.token]);
        return found ? { ...found, ...(own ? {} : { idColumn: ref.token }) } : null;
    }
    const present = (v) => v !== undefined && v !== null && v !== '' && (typeof v === 'string' || typeof v === 'number');
    const own = ref.id ? row?.[ref.id] : (value !== undefined ? value : row?.[column.key]);
    if (present(own))
        return { entity: ref.entity, id: String(own), ...(ref.id ? { idColumn: ref.id } : {}) };
    for (const alt of ref.also ?? []) {
        const v = row?.[alt.id];
        if (present(v))
            return { entity: alt.entity, id: String(v), idColumn: alt.id };
    }
    return null;
}
/** The record tab a column's link opens on: its `ref.tab`, for a record of the ref's own entity only. */
export function refTab(column, target) {
    const ref = column.ref;
    return ref && ref.token === undefined && ref.tab && target?.entity === ref.entity ? ref.tab : undefined;
}
export const ACTION_CHANGES = ['direct', 'correction', 'decision'];
export const ALERT_KINDS = ['act', 'wait', 'info'];
/** Whether a record's tab is drawn for this row: its `when` holds (a tab with none always is). */
export function tabShown(tab, row) {
    return holds(row ?? {}, tab.when);
}
/**
 * The cards a record's tab places for this row, by `{module}.{key}`: every name, and every
 * `{key, when}` whose `when` holds — none at all when the tab's own `when` does not. One reading,
 * for the page that draws them and the hosts that plan them (member app and portal alike).
 */
export function cardKeys(tab, row) {
    if (!tabShown(tab, row))
        return [];
    const out = [];
    for (const c of tab.cards ?? []) {
        const key = typeof c === 'string' ? c : c?.key;
        if (typeof key !== 'string' || out.includes(key))
            continue;
        if (typeof c !== 'string' && !holds(row ?? {}, c.when))
            continue;
        out.push(key);
    }
    return out;
}
/** Every card a record's tab names, whatever its conditions — for checks that ask what a tab is made of. */
export function namedCards(tab) {
    return (tab.cards ?? []).map((c) => (typeof c === 'string' ? c : c?.key)).filter((k) => typeof k === 'string');
}
export const PANEL_DRAWS = ['list', 'facts', 'count', 'alert', 'action', 'figures', 'check', 'lanes', 'tile', 'bars', 'trend', 'meter', 'conversation'];
export const EXTENSION_KINDS = ['card', 'alert', 'fact', 'tab', 'action', 'status', 'relation'];
export const MAX_PANEL_SERIES = 4;
export const SERIES_TONES = ['blue', 'violet', 'green', 'amber'];
export const HUB_DRAWS = ['tiles', 'feed', 'cards', 'buckets', 'timeline'];
/** A path this renderer will put a session token on. Must be a path, and only a path. */
const SOURCE_PATH = /^\/[^\s?#]*$/;
/**
 * What may be substituted into a path, and it is deliberately narrower than "a string".
 *
 * A row is an answer from a service, and an answer is not a place this code can verify. Dropped
 * into `/tenant/modules/{key}` unescaped, a value of `../../members/x` turns "turn this module
 * off" into a DELETE somewhere else entirely, under the person's own permissions. Escaping is not
 * enough on its own: `.` and `..` survive encodeURIComponent and a URL still resolves them. A key
 * is a name, so anything that is not one is refused rather than escaped.
 */
const NAME = /^[A-Za-z0-9][A-Za-z0-9_.-]*$/;
const PLACEHOLDER = /\{([a-z][a-z0-9_]*)\}/g;
/**
 * What this version cannot draw faithfully. Reported before anything is rendered, so a screen is
 * either drawn as written or refused with the reason, never approximated.
 */
export function inspect(manifest, hasComponent) {
    const problems = [];
    // A manifest validated at build time can still arrive as something else entirely: a proxy's
    // HTML error page parses as a string, a truncated body as a fragment. This is the check the
    // build-time schema would give, in the one place the document is not the one that was checked.
    if (!manifest || typeof manifest !== 'object' || Array.isArray(manifest)) {
        return [{ code: 'MALFORMED', detail: 'this is not a screen manifest' }];
    }
    if (manifest.columns !== undefined && !Array.isArray(manifest.columns)) {
        problems.push({ code: 'MALFORMED', detail: 'columns is not a list of columns' });
    }
    for (const column of Array.isArray(manifest.columns) ? manifest.columns : []) {
        if (!column || typeof column.key !== 'string' || typeof column.label !== 'string') {
            problems.push({ code: 'MALFORMED', detail: 'a column is missing its key or its label' });
        }
    }
    if (manifest.source?.path !== undefined && !SOURCE_PATH.test(manifest.source.path)) {
        // The session puts this unit's token on whatever this addresses. It may choose which path on
        // an already-trusted origin is asked, and it may not choose the origin.
        problems.push({ code: 'BAD_SOURCE_PATH', detail: `"${manifest.source.path}" is not a path on this unit` });
    }
    if (manifest.source?.unit) {
        problems.push({ code: 'UNSUPPORTED_SOURCE_UNIT', detail: `reading from unit "${manifest.source.unit}" needs an address this version does not resolve` });
    }
    // A file fills a list (#28); on any other kind there are no rows for it to land among.
    // A board draws a list's rows (list standard §12); on any other kind there are none to lay out.
    if (manifest.calendar && manifest.kind !== 'list') {
        problems.push({ code: 'MALFORMED', detail: 'a calendar lays a list\'s rows on their days; this screen is not a list' });
    }
    if (manifest.board && manifest.kind !== 'list') {
        problems.push({ code: 'MALFORMED', detail: 'a board lays out a list\'s rows; this screen is not a list' });
    }
    if (manifest.timeline && manifest.kind !== 'list') {
        problems.push({ code: 'MALFORMED', detail: 'a timeline lays out a list\'s rows; this screen is not a list' });
    }
    if (manifest.import && (manifest.kind !== 'list' || typeof manifest.import.path !== 'string' || !manifest.import.path.startsWith('/'))) {
        problems.push({ code: 'MALFORMED', detail: manifest.kind !== 'list' ? 'an import fills a list; this screen is not one' : 'an import names the route that reads its file' });
    }
    if (problems.length)
        return problems;
    if (manifest.kind === 'custom') {
        if (typeof manifest.component !== 'string' || !manifest.component) {
            problems.push({ code: 'MALFORMED', detail: 'a custom screen with no component has nothing to draw' });
        }
        else if (!hasComponent(manifest.component)) {
            problems.push({ code: 'UNKNOWN_COMPONENT', detail: `this screen names "${manifest.component}", which no unit has registered` });
        }
        return problems;
    }
    if (manifest.kind === 'record')
        return [...problems, ...inspectRecord(manifest)];
    if (manifest.kind === 'card')
        return [...problems, ...inspectCard(manifest)];
    if (manifest.kind === 'hub')
        return [...problems, ...inspectHub(manifest)];
    if (manifest.kind === 'report')
        return [...problems, ...inspectReport(manifest)];
    if (manifest.kind === 'settings')
        problems.push(...inspectSettings(manifest));
    if (manifest.kind !== 'list' && manifest.kind !== 'form' && manifest.kind !== 'settings') {
        problems.push({ code: 'UNSUPPORTED_KIND', detail: `screens of kind "${String(manifest.kind)}" are not drawn by this version${String(manifest.kind) === 'detail' ? ' — one record\'s page is kind "record"' : ''}` });
        return problems;
    }
    if (manifest.kind === 'list' && !manifest.source?.path) {
        problems.push({ code: 'NO_SOURCE', detail: 'a list with no source would be an empty table pretending to be data' });
    }
    for (const column of manifest.columns ?? []) {
        if (column.type && !COLUMN_TYPES.includes(column.type)) {
            problems.push({ code: 'UNSUPPORTED_COLUMN', column: column.key, detail: `column type "${column.type}" is not one this version formats` });
        }
    }
    // Two primaries is not a stronger screen; it is a screen that has not decided. Refused here
    // rather than resolved quietly, so the module author sees it on the screen they are building.
    const primaries = (manifest.actions ?? []).filter((a) => a.primary && !isRowAction(a));
    // An action reached only from a lookup is not on the screen, so it cannot be what the screen is for.
    for (const a of manifest.actions ?? []) {
        if (a.lookup_only && (a.primary || isRowAction(a))) {
            problems.push({ code: 'MALFORMED', detail: `action "${a.key}" is reached only from a lookup, so it is neither primary nor a row action` });
        }
    }
    if (primaries.length > 1) {
        problems.push({
            code: 'TWO_PRIMARIES',
            detail: `${primaries.map((a) => `"${a.key}"`).join(' and ')} are both marked primary, and a screen has one`,
        });
    }
    problems.push(...inspectAppearance(manifest));
    for (const field of fieldsOf(manifest)) {
        if (!field.type || !FIELD_TYPES.includes(field.type)) {
            problems.push({ code: 'UNSUPPORTED_FIELD', field: field.key, detail: `field type "${field.type ?? 'none'}" has no control in this version` });
        }
        // Words that belong to one control, on another, would be read by nothing: refused, not ignored.
        if ((field.capture !== undefined || field.multiple !== undefined) && field.type !== 'file') {
            problems.push({ code: 'MALFORMED', detail: `field "${field.key}" asks for a camera or several files, which only a file field takes` });
        }
        if (field.max_files !== undefined && !(field.type === 'file' && field.multiple)) {
            problems.push({ code: 'MALFORMED', detail: `field "${field.key}" limits how many files it holds, but it holds one` });
        }
        if (field.max_seconds !== undefined && field.type !== 'audio') {
            problems.push({ code: 'MALFORMED', detail: `field "${field.key}" limits a recording, but it records nothing` });
        }
        const linesOnly = LINES_ONLY.filter((k) => field[k] !== undefined);
        if (linesOnly.length && !isLinesField(field)) {
            problems.push({ code: 'MALFORMED', detail: `field "${field.key}" says ${linesOnly.join(', ')}, which only a lines field takes` });
        }
    }
    // A lines field: its columns, formulas, totals and the head fields its choices may name (lines.ts).
    for (const action of manifest.actions ?? []) {
        const heads = (action.fields ?? []).filter((f) => !isLinesField(f)).map((f) => f.key);
        for (const field of action.fields ?? []) {
            if (!isLinesField(field))
                continue;
            for (const detail of linesDeclarationProblems(field, heads))
                problems.push({ code: 'MALFORMED', detail });
        }
    }
    // A `{name}` in a choice route is filled from an action's form or its row. A screen's own fields
    // are no action's form, and nothing reads their choices by a value — refused, not left empty.
    for (const field of Array.isArray(manifest.fields) ? manifest.fields : []) {
        const path = field?.options_from?.path;
        if (typeof path === 'string' && namedIn(path).length) {
            problems.push({ code: 'MALFORMED', detail: `field "${field.key}" of the screen reads its choices from "${path}", which names a value only an action's form or row can give` });
        }
    }
    for (const [slot, { component }] of Object.entries(manifest.slots ?? {})) {
        if (!hasComponent(component)) {
            problems.push({ code: 'UNKNOWN_COMPONENT', detail: `slot "${slot}" names "${component}", which no unit has registered` });
        }
    }
    for (const action of manifest.actions ?? []) {
        if (action.component && !hasComponent(action.component)) {
            problems.push({ code: 'UNKNOWN_COMPONENT', detail: `action "${action.key}" names "${action.component}", which no unit has registered` });
        }
    }
    return problems;
}
/**
 * The half of inspect() that is about how a list looks: the layout, the card map, and everything
 * counted from the rows.
 *
 * One rule runs through all of it. The statistics, the tab counts, the search and the filters are
 * all computed on the client from the rows that were loaded, so each is a statement about the
 * entity only when the loaded rows are the whole of it. Against a paginated route they are
 * statements about a page wearing the costume of a total: a tab reading "Archived 3" over 50 of
 * 4,000 parties is not a small error, it is a different sentence. Nothing here can tell from a
 * path whether a route pages — so the manifest declares `source.complete`, and without that
 * declaration the vocabulary is refused rather than drawn wrongly.
 *
 * The rest is the same refusal in smaller forms: a tone this version has no token for, a display
 * kind it cannot draw, a card whose title names a column that is not there. Each would render as
 * something — an unstyled pill, a plain cell, a card with an empty heading — and rendering as
 * something is how a screen ends up lying quietly.
 */
function inspectAppearance(manifest) {
    const problems = [];
    const columns = Array.isArray(manifest.columns) ? manifest.columns : [];
    const declared = new Set(columns.map((c) => c?.key));
    const names = (what, key) => {
        if (typeof key !== 'string' || !declared.has(key)) {
            problems.push({ code: 'MALFORMED', detail: `${what} names "${String(key)}", which is not a declared column` });
        }
    };
    for (const column of columns) {
        if (!column)
            continue;
        if (column.display && !DISPLAY_KINDS.includes(column.display)) {
            problems.push({ code: 'UNSUPPORTED_COLUMN', column: column.key, detail: `display "${column.display}" is not one this version draws` });
        }
        for (const channel of column.channels ?? []) {
            if (column.display !== 'phone') {
                problems.push({ code: 'UNSUPPORTED_COLUMN', column: column.key, detail: '`channels` is for a `phone` column' });
                break;
            }
            if (!PHONE_CHANNELS.includes(channel)) {
                problems.push({ code: 'UNSUPPORTED_COLUMN', column: column.key, detail: `channel "${String(channel)}" is not one this version draws` });
            }
        }
        for (const [value, tone] of Object.entries(column.tones ?? {})) {
            if (!TONES.includes(tone)) {
                problems.push({ code: 'UNSUPPORTED_COLUMN', column: column.key, detail: `"${value}" asks for tone "${tone}", which is not in the palette` });
            }
        }
        if (column.detail !== undefined)
            names(`column "${column.key}" detail`, column.detail);
    }
    if (manifest.layout !== undefined && !LAYOUTS.includes(manifest.layout)) {
        problems.push({ code: 'MALFORMED', detail: `layout "${manifest.layout}" is not one this version draws` });
    }
    if (manifest.layout === 'cards' && !manifest.card) {
        problems.push({ code: 'MALFORMED', detail: 'a cards layout with no card map has nothing to put on a card' });
    }
    if (manifest.layout === 'calendar' && !manifest.calendar) {
        problems.push({ code: 'MALFORMED', detail: 'a calendar layout with no calendar block has no day to put a row on' });
    }
    if (manifest.card) {
        // Without a title a card is a box of fields, and which field leads is exactly the decision the
        // card map exists to record.
        if (typeof manifest.card.title !== 'string' || !manifest.card.title) {
            problems.push({ code: 'MALFORMED', detail: 'the card map has no title' });
        }
        else {
            names('the card title', manifest.card.title);
        }
        for (const part of ['subtitle', 'badge', 'description']) {
            if (manifest.card[part] !== undefined)
                names(`the card ${part}`, manifest.card[part]);
        }
        for (const key of manifest.card.stats ?? [])
            names('a card statistic', key);
    }
    // What is counted from the rows, and therefore what needs the rows to be all of them.
    const derived = [];
    if (manifest.stats?.length)
        derived.push('stats');
    if (manifest.tabs)
        derived.push('tabs');
    // A lane's count and sum are figures about the rows too: over a page they would be the page's.
    if (manifest.board)
        derived.push('board');
    if (manifest.search)
        derived.push('search');
    if ((manifest.filters ?? []).some((f) => f && isApplicable(f)))
        derived.push('filters');
    // A windowed source answers every row of the days in view: the search, the filters and the tabs
    // apply to that answer as on a complete list (calendar design §2.2). Its figures are another
    // matter — a count of the days drawn is not a count of the entity — so statistics still need the whole.
    const windowed = Boolean(manifest.source?.window) && !derived.includes('stats') && !derived.includes('board');
    if (derived.length && !manifest.source?.complete && !manifest.source?.query && !windowed) {
        problems.push({
            code: 'INCOMPLETE_SOURCE',
            detail: `${derived.join(', ')} ${derived.length === 1 ? 'is' : 'are'} counted from the rows that were loaded, and "${manifest.source?.path ?? 'this source'}" has not declared that those are all of them`,
        });
    }
    for (const stat of manifest.stats ?? []) {
        if (!stat || !AGGREGATES.includes(stat.aggregate)) {
            problems.push({ code: 'MALFORMED', detail: `statistic "${stat?.key ?? '?'}" aggregates by "${stat?.aggregate}", which is not one this version computes` });
            continue;
        }
        // A percentage of everything is 100%, which is a fact about arithmetic, not about the data.
        if (stat.aggregate === 'percent' && !stat.where) {
            problems.push({ code: 'MALFORMED', detail: `statistic "${stat.key}" is a percentage of every row, which is always 100%` });
        }
        if (stat.aggregate === 'oldest') {
            if (!stat.column) {
                problems.push({ code: 'MALFORMED', detail: `statistic "${stat.key}" asks for the oldest with no column to read it from` });
            }
            else {
                names(`statistic "${stat.key}" column`, stat.column);
                const read = columns.find((c) => c?.key === stat.column);
                const type = (read?.type ?? 'text');
                if (read && type !== 'date' && type !== 'datetime') {
                    problems.push({ code: 'MALFORMED', detail: `statistic "${stat.key}" asks for the oldest "${stat.column}", whose type "${type}" is not a date` });
                }
            }
        }
        if (stat.aggregate === 'sum') {
            if (!stat.column) {
                problems.push({ code: 'MALFORMED', detail: `statistic "${stat.key}" is a sum with no column to add` });
            }
            else {
                names(`statistic "${stat.key}" column`, stat.column);
                const summed = columns.find((c) => c?.key === stat.column);
                const type = (summed?.type ?? 'text');
                if (summed && !SUMMABLE_COLUMN_TYPES.includes(type)) {
                    problems.push({ code: 'MALFORMED', detail: `statistic "${stat.key}" sums column "${stat.column}", whose type "${type}" is not numeric` });
                }
            }
        }
    }
    if (manifest.tabs) {
        names('tabs.by', manifest.tabs.by);
        const from = manifest.tabs.values_from;
        if (from !== undefined) {
            // A catalogue is read from the module's own unit with the viewer's token, like a source.
            if (!from || typeof from.path !== 'string' || !SOURCE_PATH.test(from.path) || !from.value || !from.label) {
                problems.push({ code: 'MALFORMED', detail: 'tabs.values_from needs a path on this unit, and the properties that are each value and its name' });
            }
            // Resolved, the catalogue's rows become `values` and `values_from` goes (resolveTabs): both
            // at once is a manifest saying two different things about which tabs exist.
            if (manifest.tabs.values !== undefined) {
                problems.push({ code: 'MALFORMED', detail: 'tabs declare their values and also read them from a catalogue' });
            }
        }
        else if (!Array.isArray(manifest.tabs.values) || !manifest.tabs.values.length) {
            // Deriving them from the data instead would hide a tab exactly when its emptiness is the news.
            problems.push({ code: 'MALFORMED', detail: 'tabs declare no values, and this version does not take them from the data' });
        }
    }
    if (manifest.board)
        problems.push(...inspectBoard(manifest, names));
    problems.push(...inspectTimeline(manifest));
    if (manifest.calendar)
        problems.push(...inspectCalendar(manifest, names));
    if (manifest.source?.window && manifest.source.complete) {
        problems.push({ code: 'MALFORMED', detail: 'the source answers whole and answers a window; a whole answer has no window to ask' });
    }
    if (manifest.search) {
        if (!Array.isArray(manifest.search.columns) || !manifest.search.columns.length) {
            problems.push({ code: 'MALFORMED', detail: 'search names no columns' });
        }
        for (const key of manifest.search.columns ?? [])
            names('a search column', key);
    }
    const filtered = new Set();
    for (const filter of manifest.filters ?? []) {
        if (filter && filtered.has(filter.column)) {
            problems.push({ code: 'MALFORMED', detail: `two filters name the column "${filter.column}"` });
        }
        filtered.add(filter?.column);
        // A dimension the screen has not got names no column — that is what it says — and carries
        // no control: a choice or an operator on it would narrow by something the rows do not hold.
        if (filter && !isApplicable(filter)) {
            if (typeof filter.column !== 'string' || !filter.column || typeof filter.label !== 'string' || !filter.label) {
                problems.push({ code: 'MALFORMED', detail: 'a filter that does not apply still names the dimension it is about, and says so in words' });
            }
            if (filter.quick === true || filter.operators?.length || filter.values?.length || filter.values_from) {
                problems.push({ code: 'MALFORMED', detail: `filter "${filter.column}" does not apply, and declares choices or operators as if it did` });
            }
            continue;
        }
        names('a filter', filter?.column);
        const column = columns.find((c) => c?.key === filter?.column);
        const type = (column?.type ?? 'text');
        for (const op of filter?.operators ?? []) {
            if (!FILTER_OPERATORS.includes(op)) {
                problems.push({ code: 'MALFORMED', detail: `filter "${filter.column}" names operator "${op}", which is not one this version compares with` });
            }
            else if (column && !OPERATORS_FOR[type].includes(op)) {
                problems.push({ code: 'MALFORMED', detail: `filter "${filter.column}" offers "${op}", which a ${type} column cannot be compared with` });
            }
        }
        const from = filter?.values_from;
        if (from !== undefined) {
            if (!from || typeof from.path !== 'string' || !SOURCE_PATH.test(from.path) || !from.value) {
                problems.push({ code: 'MALFORMED', detail: `filter "${filter.column}" reads its values from a catalogue, which needs a path on this unit and the property that is each value` });
            }
            if (filter.values !== undefined) {
                problems.push({ code: 'MALFORMED', detail: `filter "${filter.column}" declares its values and also reads them from a catalogue` });
            }
        }
        // A page of rows cannot say which values exist, so a filter on a paged source must be told.
        if (filter && manifest.source?.query && !filter.values?.length && !from && (isQuick(filter) || filter.operators?.includes('in'))) {
            problems.push({ code: 'MALFORMED', detail: `filter "${filter.column}" on a paged source declares no values, and one page cannot say which exist` });
        }
    }
    if (manifest.sort) {
        if (!Array.isArray(manifest.sort.columns) || !manifest.sort.columns.length) {
            problems.push({ code: 'MALFORMED', detail: 'sort names no columns' });
        }
        for (const key of manifest.sort.columns ?? []) {
            names('a sort column', key);
            const column = columns.find((c) => c?.key === key);
            const type = (column?.type ?? 'text');
            if (column && !SORTABLE_COLUMN_TYPES.includes(type)) {
                problems.push({ code: 'MALFORMED', detail: `sort names "${key}", and a ${type} column has no order` });
            }
        }
        if (manifest.sort.default !== undefined) {
            const key = manifest.sort.default.replace(/^-/, '');
            if (!(manifest.sort.columns ?? []).includes(key)) {
                problems.push({ code: 'MALFORMED', detail: `the default sort "${manifest.sort.default}" is not one of the sort columns` });
            }
        }
    }
    if (manifest.page) {
        const sizes = manifest.page.sizes ?? [...PAGE_SIZES];
        if (!sizes.includes(manifest.page.size)) {
            problems.push({ code: 'MALFORMED', detail: `page size ${manifest.page.size} is not one of the sizes offered` });
        }
    }
    problems.push(...inspectExport(manifest));
    for (const action of manifest.actions ?? []) {
        // A selection handed to a page (bulk.open): a row action with a screen, and no request of its own.
        const opensSelection = typeof action?.bulk === 'object' && action.bulk !== null && action.bulk.open === true;
        if (opensSelection && (action.scope !== 'row' || !action.screen || action.request)) {
            problems.push({ code: 'MALFORMED', detail: `action "${action.key}" opens its page with the selection (bulk.open): a row action with a screen and no request` });
        }
        else if (action?.bulk && !opensSelection && (action.scope !== 'row' || !action.request)) {
            problems.push({ code: 'MALFORMED', detail: `action "${action.key}" is bulk, and only a row action with a request can run once per selected row` });
        }
        if (action?.bulk) {
            const n = action.fields?.length ?? 0;
            const asks = typeof action.bulk === 'object' && action.bulk !== null && action.bulk.fields === true;
            if (n && !asks)
                problems.push({ code: 'MALFORMED', detail: `action "${action.key}" asks for fields and runs over a selection without saying the answers are asked once (bulk.fields)` });
            if (asks && !n)
                problems.push({ code: 'MALFORMED', detail: `action "${action.key}" says its bulk run asks its fields, and it has none` });
            const cap = bulkFieldCap(action);
            if (asks && n > cap)
                problems.push({ code: 'MALFORMED', detail: `action "${action.key}" asks ${n} fields over a selection; a bulk confirm asks at most ${cap}${cap < BULK_FORM_FIELDS ? ' beside its preview' : ''}` });
        }
        if (action?.preview !== undefined)
            problems.push(...inspectFormPreview(action));
        if (action?.fields_from !== undefined)
            problems.push(...inspectFieldsFrom(action));
        if (action)
            problems.push(...inspectOptionsFrom(action, manifest));
        if (action?.then !== undefined && !THEN_VALUES.includes(action.then)) {
            problems.push({ code: 'MALFORMED', detail: `action "${action.key}" answers "then: ${String(action.then)}", which this version does not act on` });
        }
        // A secret goes one way: an answer shown after a form that carried one could be the secret back.
        if (action?.then === 'reveal' && (action.fields ?? []).some(isSecret)) {
            problems.push({ code: 'MALFORMED', detail: `action "${action.key}" carries a secret and shows its answer (then: reveal); a secret is written and never shown back` });
        }
        // Opening the record just made needs a list whose rows are records: `entity` names them.
        if (action?.then === 'open' && !action.opens && (!manifest.entity || isRowAction(action))) {
            problems.push({ code: 'MALFORMED', detail: `action "${action.key}" opens what it made, and ${manifest.entity ? 'a row action names what it makes (opens)' : 'this list does not say what its rows are (entity)'}` });
        }
        if (action?.opens !== undefined && (action.then !== 'open' || !/^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/.test(action.opens))) {
            problems.push({ code: 'MALFORMED', detail: `action "${action.key}" names what it opens (${String(action.opens)}): only with then: "open", as {module}.{entity}` });
        }
    }
    if (manifest.empty) {
        if (typeof manifest.empty.label !== 'string' || !manifest.empty.label) {
            problems.push({ code: 'MALFORMED', detail: 'the empty state has no words' });
        }
        const waits = manifest.empty.catalogue;
        if (waits !== undefined && (typeof waits.path !== 'string' || !waits.path.startsWith('/') || !waits.label || !waits.action?.label || !/^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/.test(waits.action?.screen ?? ''))) {
            problems.push({ code: 'MALFORMED', detail: 'the empty state waits on a catalogue without a path, its words, or the screen that defines it (module.screen)' });
        }
        if (manifest.empty.action !== undefined) {
            const named = (manifest.actions ?? []).find((a) => a.key === manifest.empty.action);
            if (!named || isRowAction(named)) {
                problems.push({ code: 'MALFORMED', detail: `the empty state offers "${manifest.empty.action}", which is not an action of this screen about the list` });
            }
        }
    }
    for (const rule of manifest.rows?.emphasis ?? []) {
        if (!rule || !EMPHASIS_TONES.includes(rule.tone)) {
            problems.push({ code: 'MALFORMED', detail: `a row emphasis asks for tone "${String(rule?.tone)}"; a row is emphasised in red or amber only` });
            continue;
        }
        if (typeof rule.label !== 'string' || !rule.label)
            problems.push({ code: 'MALFORMED', detail: 'a row emphasis has no words, and a tone is never colour alone' });
        for (const c of [rule.when ?? []].flat())
            names('a row emphasis', c?.key);
    }
    for (const column of columns) {
        for (const mark of column?.marks ?? []) {
            if (typeof mark?.label !== 'string' || !mark.label)
                problems.push({ code: 'MALFORMED', detail: `a mark on "${column.key}" has no words, and a tone is never colour alone` });
            if (mark?.tone !== undefined && !TONES.includes(mark.tone))
                problems.push({ code: 'MALFORMED', detail: `a mark on "${column.key}" asks for tone "${String(mark.tone)}"` });
            for (const c of [mark?.when ?? []].flat())
                names(`a mark on "${column.key}"`, c?.key);
        }
    }
    for (const key of manifest.export?.totals ?? []) {
        names('an export total', key);
        const column = columns.find((c) => c?.key === key);
        const type = (column?.type ?? 'text');
        if (column && !SUMMABLE_COLUMN_TYPES.includes(type)) {
            problems.push({ code: 'MALFORMED', detail: `the export totals "${key}", whose type "${type}" is not numeric` });
        }
    }
    // On a paged source the query is the only honest way to sort, filter, page or export: a route
    // that ignores `?sort=` under a header showing a sort arrow is the same lie as a tab count over
    // one page. So each of these needs the route's word that it reads the query.
    // A windowed source answers every row of the days asked (calendar design §2.2): the list sorts,
    // pages and exports that answer in hand, as it does a complete one, so those need no query.
    if (manifest.source && !manifest.source.complete && !manifest.source.query) {
        const windowed = Boolean(manifest.source.window);
        const asked = [];
        if (manifest.sort && !windowed)
            asked.push('sort');
        if (manifest.page && !windowed)
            asked.push('page');
        if ((manifest.filters ?? []).some((f) => f?.operators?.length))
            asked.push('filter operators');
        if (manifest.export && !windowed)
            asked.push('export');
        if (asked.length) {
            problems.push({ code: 'INCOMPLETE_SOURCE', detail: `${asked.join(', ')} ${asked.length === 1 ? 'needs' : 'need'} the route to read the query, and "${manifest.source.path}" has declared neither that it answers whole nor that it answers the query` });
        }
    }
    return problems;
}
/**
 * A list's board (list standard §12): every name it gives is a column or a row action this version
 * can run a drop with. A lane of blank cards, a sum of words or a drop that does nothing would each
 * draw as something, which is why each is refused instead.
 */
function inspectBoard(manifest, names) {
    const problems = [];
    const board = manifest.board;
    const columns = Array.isArray(manifest.columns) ? manifest.columns : [];
    names('the board', board.by);
    if (!board.card || typeof board.card !== 'object')
        problems.push({ code: 'MALFORMED', detail: 'the board has no card map, and a card with no title is a box of fields' });
    else {
        names('the board card title', board.card.title);
        for (const part of ['subtitle', 'badge'])
            if (board.card[part] !== undefined)
                names(`the board card ${part}`, board.card[part]);
        for (const key of board.card.stats ?? [])
            names('a board card statistic', key);
    }
    const from = board.values_from;
    if (from !== undefined) {
        if (!from || typeof from.path !== 'string' || !SOURCE_PATH.test(from.path) || !from.value || !from.label) {
            problems.push({ code: 'MALFORMED', detail: 'the board reads its lanes from a route, and needs a path on this unit and the properties that are each lane and its name' });
        }
        if (board.values !== undefined)
            problems.push({ code: 'MALFORMED', detail: 'the board declares its lanes and also reads them from a route' });
    }
    else if (!Array.isArray(board.values) || !board.values.length) {
        // Lanes taken from the data would hide a lane exactly when its emptiness is the news.
        problems.push({ code: 'MALFORMED', detail: 'the board declares no lanes, and this version does not take them from the data' });
    }
    if (board.sum !== undefined) {
        names('the board sum', board.sum);
        const summed = columns.find((c) => c?.key === board.sum);
        if (summed && !SUMMABLE_COLUMN_TYPES.includes((summed.type ?? 'text'))) {
            problems.push({ code: 'MALFORMED', detail: `the board sums "${board.sum}", whose type "${summed.type ?? 'text'}" is not numeric` });
        }
    }
    if (board.opens !== undefined && board.opens !== 'table' && board.opens !== 'board') {
        problems.push({ code: 'MALFORMED', detail: `the board opens as "${String(board.opens)}"; it opens as the table or the board` });
    }
    if (board.move) {
        const action = (manifest.actions ?? []).find((a) => a?.key === board.move.action);
        const field = board.move.field ?? board.by;
        if (!action || !isRowAction(action) || !action.request || action.component || action.screen) {
            problems.push({ code: 'MALFORMED', detail: `a drop on the board runs "${board.move.action}", which is not a row action of this screen with a request and a declared form` });
        }
        else {
            if (!(action.fields ?? []).some((f) => f?.key === field))
                problems.push({ code: 'MALFORMED', detail: `a drop on the board runs "${action.key}", which has no field "${field}" for the lane` });
            if (action.then !== undefined && action.then !== 'reload')
                problems.push({ code: 'MALFORMED', detail: `a drop on the board runs "${action.key}", which answers "then: ${action.then}"; a move is read again where it was dropped` });
        }
    }
    return problems;
}
/**
 * A list's calendar (list standard §13): every part a declared column of the right type, a move this
 * version could run, and rows that are all of them — or all of them in the days in view. A day with
 * nothing on it because the source handed back a page would be a calendar quietly lying.
 */
function inspectCalendar(manifest, names) {
    const problems = [];
    const cal = manifest.calendar;
    const columns = Array.isArray(manifest.columns) ? manifest.columns : [];
    const typeOf = (key) => (columns.find((c) => c?.key === key)?.type ?? 'text');
    const has = (key) => columns.some((c) => c?.key === key);
    names('the calendar date', cal.date);
    names('the calendar title', cal.title);
    for (const part of ['date', 'end']) {
        if (cal[part] === undefined)
            continue;
        if (part === 'end')
            names('the calendar end', cal.end);
        if (has(cal[part]) && typeOf(cal[part]) !== 'date' && typeOf(cal[part]) !== 'datetime') {
            problems.push({ code: 'MALFORMED', detail: `the calendar's ${part} is "${String(cal[part])}", whose type "${typeOf(cal[part])}" is not a date` });
        }
    }
    if (cal.tone !== undefined) {
        names('the calendar tone', cal.tone);
        const toned = columns.find((c) => c?.key === cal.tone);
        if (toned && !Object.keys(toned.tones ?? {}).length)
            problems.push({ code: 'MALFORMED', detail: `the calendar takes its tone from "${cal.tone}", which declares no tones` });
    }
    if (cal.views !== undefined && (!Array.isArray(cal.views) || !cal.views.length || cal.views.some((v) => v !== 'month' && v !== 'week' && v !== 'agenda'))) {
        problems.push({ code: 'MALFORMED', detail: 'the calendar offers a view this version does not know; it knows month, week and agenda' });
    }
    if (cal.move) {
        const action = (manifest.actions ?? []).find((a) => a?.key === cal.move.action);
        if (!action || !isRowAction(action) || !action.request || action.component || action.screen || !(action.fields ?? []).some((f) => f?.key === cal.move.field)) {
            problems.push({ code: 'MALFORMED', detail: `a move on the calendar runs "${cal.move.action}", which is not a row action of this screen with a request and a field "${cal.move.field}" for the day` });
        }
    }
    if (manifest.source && !manifest.source.complete && !manifest.source.window) {
        problems.push({ code: 'INCOMPLETE_SOURCE', detail: `a calendar draws every row of the days in view, and "${manifest.source.path}" has declared neither that it answers whole nor that it answers a window` });
    }
    return problems;
}
/**
 * Each field of an action whose choices are read from a route (`options_from.path`): a path on the
 * unit, and each `{name}` in it a value that can be there — a field of this form, or on a row action
 * a column of the screen (the row's property). A name with nothing behind it would leave the field
 * offering nothing for ever.
 */
function inspectOptionsFrom(action, manifest) {
    const problems = [];
    const own = new Set((action.fields ?? []).map((f) => f?.key));
    const columns = new Set((Array.isArray(manifest.columns) ? manifest.columns : []).map((c) => c?.key));
    for (const field of action.fields ?? []) {
        const path = field?.options_from?.path;
        if (path === undefined)
            continue;
        if (typeof path !== 'string' || !OPTIONS_PATH.test(path)) {
            problems.push({ code: 'BAD_SOURCE_PATH', detail: `field "${field.key}" of action "${action.key}" reads its choices from "${String(path)}", which is not a path on this unit` });
            continue;
        }
        for (const key of namedIn(path)) {
            if (key === field.key)
                problems.push({ code: 'MALFORMED', detail: `field "${field.key}" of action "${action.key}" reads its choices by its own value "{${key}}"` });
            else if (!own.has(key) && !(isRowAction(action) && columns.has(key))) {
                problems.push({ code: 'MALFORMED', detail: `field "${field.key}" of action "${action.key}" reads its choices by "{${key}}", which is not a field of its form${isRowAction(action) ? ' nor a column of its row' : ''}` });
            }
        }
    }
    return problems;
}
/**
 * A form's preview, against itself: a preview is the declared form's, sent where the form is sent —
 * so it needs fields and a request, and a form the module draws itself (`component`, `screen`) has
 * nobody to draw it. Its facts are one to four columns. A bulk run that asks the same fields in its
 * confirm (`bulk.fields`) previews there too (`bulk.preview`): the route that answers the one form
 * answers every row, and the confirm would otherwise run blind on answers the form would have shown.
 */
function inspectFormPreview(action) {
    const problems = [];
    const say = (detail) => problems.push({ code: 'MALFORMED', detail: `action "${action.key}" ${detail}` });
    const preview = action.preview;
    if (!preview || typeof preview !== 'object' || !Array.isArray(preview.columns) || preview.columns.length < 1 || preview.columns.length > FORM_PREVIEW_COLUMNS) {
        say(`previews its form without saying what to draw of the answer (preview.columns, 1 to ${FORM_PREVIEW_COLUMNS})`);
    }
    if (!action.fields?.length)
        say('previews a form, and it has no fields');
    if (!action.request)
        say('previews a form that sends nothing (no request)');
    if (action.component || action.screen)
        say('previews a declared form, and this action draws its own');
    if (typeof action.bulk === 'object' && action.bulk !== null && action.bulk.fields === true && !action.bulk.preview) {
        say('previews its form, and its bulk run asks the same fields without a preview (bulk.preview)');
    }
    return problems;
}
/** An action that asks a route for more fields: where, and how many may come — the room the frame is chosen for. */
function inspectFieldsFrom(action) {
    const from = action.fields_from;
    if (!from || typeof from.path !== 'string' || !SOURCE_PATH.test(from.path)) {
        return [{ code: 'MALFORMED', detail: `action "${action.key}" asks a route for more fields and names no path on this unit` }];
    }
    if (!Number.isInteger(from.max) || from.max < 1 || from.max > PAGE_AFTER) {
        return [{ code: 'MALFORMED', detail: `action "${action.key}" asks a route for more fields without saying how many may come (max, 1 to ${PAGE_AFTER})` }];
    }
    if (action.component || action.screen)
        return [{ code: 'MALFORMED', detail: `action "${action.key}" draws its own form, and fields from a route complete a declared one` }];
    const own = new Set((action.fields ?? []).map((f) => f?.key));
    // A name the form has not got is filled from the row on a row action; about the list, there is no row.
    for (const [, key] of from.path.matchAll(PLACEHOLDER)) {
        if (!own.has(key) && !isRowAction(action))
            return [{ code: 'MALFORMED', detail: `action "${action.key}" asks a route named by "{${key}}", which is not a field of its form` }];
    }
    return [];
}
/**
 * What a record page asks for that this version cannot draw faithfully — before anything is fetched.
 * The build checks the same names (loadUnit); this is the check in the one place the manifest may
 * not be the one that was checked.
 */
function inspectRecord(manifest) {
    const problems = [];
    const r = manifest.record;
    if (!r || typeof r !== 'object' || typeof r.title !== 'string')
        return [{ code: 'MALFORMED', detail: 'a record screen with no record part has no head to draw' }];
    if (!manifest.source?.path || !manifest.source.path.includes('{id}')) {
        problems.push({ code: 'NO_SOURCE', detail: 'a record is read by its id, and this source path carries no {id}' });
    }
    const declared = new Set((manifest.columns ?? []).map((c) => c?.key));
    const names = (what, key) => {
        if (key !== undefined && !declared.has(key))
            problems.push({ code: 'MALFORMED', detail: `${what} names "${key}", which is not a declared column` });
    };
    names('the record title', r.title);
    names('the record number', r.number);
    names('the record state', r.state);
    for (const k of r.subtitle ?? [])
        names('the subtitle', subtitleKey(k));
    for (const k of r.chips ?? [])
        names('a chip', subtitleKey(k));
    for (const s of r.statuses ?? [])
        names('a status', s.key);
    for (const t of r.tabs ?? [])
        for (const k of t.table?.totals ?? [])
            names(`tab "${t.key}" totals`, k);
    // A fact names its column, and the columns that say since when it holds and by which decision.
    const fact = (what, f) => {
        names(what, factKey(f));
        if (typeof f !== 'string' && f.in_force) {
            names(`${what} in force since`, f.in_force.since);
            names(`${what} decision`, f.in_force.decision);
        }
    };
    for (const f of r.facts ?? [])
        fact('a fact', f);
    for (const c of r.cards ?? [])
        for (const f of c.facts ?? [])
            fact(`card "${c.key}"`, f);
    for (const t of r.tabs ?? [])
        for (const f of t.facts ?? [])
            fact(`tab "${t.key}"`, f);
    for (const c of r.changes ?? []) {
        // A line of «What do you want to change?» opens one thing: the record's action, or a contributed correction.
        const opens = [c?.action, c?.corrects, c?.contributed].filter(Boolean).length;
        if (opens !== 1)
            problems.push({ code: 'MALFORMED', detail: `change "${String(c?.key)}" opens ${opens ? 'more than one' : 'none'} of an action, a correction request and a contributed action` });
        else if (c.contributed && (typeof c.contributed.part !== 'string' || !c.contributed.part || typeof c.contributed.action !== 'string' || !c.contributed.action))
            problems.push({ code: 'MALFORMED', detail: `change "${c.key}" opens a contributed action without naming its part and action` });
        else if (!ACTION_CHANGES.includes(c.change))
            problems.push({ code: 'MALFORMED', detail: `change "${c.key}" is a "${String(c.change)}", which is not one this version tags` });
    }
    if (r.stages) {
        names('the stages', r.stages.by);
        const from = r.stages.values_from;
        // Declared or read, never both and never neither: the stepper would not know which to believe.
        if (from !== undefined && r.stages.values !== undefined)
            problems.push({ code: 'MALFORMED', detail: 'the stages declare their steps and also read them from a route' });
        else if (from === undefined && (!Array.isArray(r.stages.values) || r.stages.values.length < 2))
            problems.push({ code: 'MALFORMED', detail: 'the stages declare no steps and read none from a route' });
        // A step that ends the path is one of the path's steps; one that is not would never be drawn done.
        else if (from === undefined)
            for (const d of r.stages.done ?? [])
                if (!r.stages.values.includes(d))
                    problems.push({ code: 'MALFORMED', detail: `the stages end on "${d}", which is not one of their steps` });
        if (from !== undefined) {
            if (!from || typeof from.path !== 'string' || !SOURCE_PATH.test(from.path) || typeof from.value !== 'string' || !from.value || typeof from.label !== 'string' || !from.label) {
                problems.push({ code: 'MALFORMED', detail: 'the stages read from a route need a path on this unit, and the properties that are each step and its name' });
            }
            else {
                for (const [, key] of from.path.matchAll(PLACEHOLDER))
                    names('the stages\' route', key);
            }
        }
    }
    for (const a of r.alerts ?? []) {
        if (!ALERT_KINDS.includes(a.kind))
            problems.push({ code: 'MALFORMED', detail: `alert "${a.key}" is of kind "${a.kind}", which is not one this version draws` });
        for (const d of [a.detail ?? []].flat())
            names(`alert "${a.key}"`, d);
        names(`alert "${a.key}"`, a.due);
        names(`alert "${a.key}"`, a.since);
    }
    for (const column of manifest.columns ?? []) {
        if (column?.type && !COLUMN_TYPES.includes(column.type)) {
            problems.push({ code: 'UNSUPPORTED_COLUMN', column: column.key, detail: `column type "${column.type}" is not one this version formats` });
        }
    }
    return problems;
}
function inspectCard(manifest) {
    const panel = manifest.panel;
    if (!panel || !PANEL_DRAWS.includes(panel.draw)) {
        return [{ code: 'MALFORMED', detail: `a card drawn as "${String(panel?.draw)}" is not one this version draws` }];
    }
    if (panel.draw !== 'action' && !manifest.source?.path)
        return [{ code: 'NO_SOURCE', detail: 'a card with no source would be an empty box pretending to be data' }];
    if (panel.draw === 'alert' && !panel.alert)
        return [{ code: 'MALFORMED', detail: 'an alert card that does not say what the alert is' }];
    if (panel.draw === 'conversation' && panel.conversation?.screen !== undefined && !/^[a-z][a-z0-9_]*$/.test(panel.conversation.screen)) {
        return [{ code: 'MALFORMED', detail: 'a conversation card opens a screen of its own module, named in lower case' }];
    }
    const declared = (k) => typeof k === 'string' && (manifest.columns ?? []).some((c) => c.key === k);
    if ((panel.series !== undefined || panel.today !== undefined) && panel.draw !== 'trend') {
        return [{ code: 'MALFORMED', detail: 'only a card drawn as trend has series or a today mark' }];
    }
    if (panel.draw === 'trend' && panel.series !== undefined) {
        // Each line is a declared column in one of the six tones, and four at most: past that the lines are a tangle.
        if (!Array.isArray(panel.series) || !panel.series.length)
            return [{ code: 'MALFORMED', detail: 'a trend\'s series is a list of one to four columns' }];
        if (panel.series.length > MAX_PANEL_SERIES)
            return [{ code: 'MALFORMED', detail: `a trend draws at most ${MAX_PANEL_SERIES} series; this one asks for ${panel.series.length}` }];
        for (const line of panel.series) {
            if (!line || !declared(line.key))
                return [{ code: 'MALFORMED', detail: `the trend's series "${String(line?.key)}" is not a declared column` }];
            if (line.tone !== undefined && !TONES.includes(line.tone))
                return [{ code: 'MALFORMED', detail: `the trend's series "${line.key}" asks for tone "${String(line.tone)}"` }];
        }
        if (new Set(panel.series.map((l) => l.key)).size !== panel.series.length)
            return [{ code: 'MALFORMED', detail: 'a trend draws each column as one series, once' }];
    }
    if (panel.today !== undefined && !declared(panel.today))
        return [{ code: 'MALFORMED', detail: `the trend's today mark "${String(panel.today)}" is not a declared column` }];
    if (panel.draw === 'bars' || panel.draw === 'trend') {
        // A chart names the columns it is drawn from; a name that is not declared would draw nothing and look like no data.
        const measures = panel.draw === 'trend' && panel.series?.length ? declared(panel.by) : declared(panel.by) && declared(panel.figure);
        if (!measures)
            return [{ code: 'MALFORMED', detail: `a card drawn as ${panel.draw} names its "by" and ${panel.draw === 'trend' ? '"figure" (or "series")' : '"figure"'} among its columns` }];
    }
    if (panel.draw === 'meter') {
        // A meter is a figure against a whole on the same row; a name that is not a column would draw a bar of nothing.
        const missing = [panel.figure, panel.of].find((k) => !k || !(manifest.columns ?? []).some((c) => c.key === k));
        if (missing !== undefined || !panel.figure || !panel.of)
            return [{ code: 'MALFORMED', detail: 'a card drawn as meter names its "figure" and its "of" among its columns' }];
    }
    const unknown = (panel.sum?.columns ?? []).find((k) => !(manifest.columns ?? []).some((c) => c.key === k));
    if (unknown)
        return [{ code: 'MALFORMED', detail: `the card adds up "${unknown}", which is not a declared column` }];
    if (panel.group_by !== undefined) {
        // Headings over a list only; a name that is not a column would put every row under «uncategorised».
        if (panel.draw !== 'list')
            return [{ code: 'MALFORMED', detail: 'only a card drawn as a list sets its rows out under headings' }];
        if (!declared(panel.group_by))
            return [{ code: 'MALFORMED', detail: `the card groups by "${String(panel.group_by)}", which is not a declared column` }];
    }
    return [];
}
/**
 * A report arrives over the wire like any manifest: a source to ask, columns this version formats,
 * parameters it has a control for. What it would draw in place of any of them is a guess.
 */
/** A list's or a report's file: formats this version writes, and a paper it can lay out (paper.ts). */
function inspectExport(manifest) {
    const problems = [];
    for (const format of manifest.export?.formats ?? []) {
        if (!EXPORT_FORMATS.includes(format)) {
            problems.push({ code: 'MALFORMED', detail: `export format "${format}" is not one this version writes` });
        }
    }
    for (const detail of paperProblems(manifest.export?.paper))
        problems.push({ code: 'MALFORMED', detail });
    return problems;
}
/** What only a list draws: on a settings page nothing would draw it (L-24). */
export const SETTINGS_FORBIDS = ['stats', 'tabs', 'filters', 'sort', 'page', 'export', 'import', 'board', 'calendar', 'timeline', 'search', 'match', 'layout', 'card', 'empty'];
function inspectSettings(manifest) {
    const problems = [];
    const m = manifest;
    for (const key of SETTINGS_FORBIDS) {
        if (m[key] !== undefined)
            problems.push({ code: 'MALFORMED', detail: `a settings page is one object in sections; "${key}" is a list's, and nothing would draw it here${key === 'empty' ? ' — its words go in settings.empty' : ''}` });
    }
    for (const a of manifest.actions ?? []) {
        if (a.bulk)
            problems.push({ code: 'MALFORMED', detail: `action "${a.key}" is bulk, and a settings page has no rows to select` });
    }
    if (!manifest.source?.path)
        problems.push({ code: 'NO_SOURCE', detail: 'a settings page with no source has nothing to read' });
    else if (/\{[a-z_]+\}/.test(manifest.source.path))
        problems.push({ code: 'MALFORMED', detail: `"${manifest.source.path}" names a value; a module's settings are read at one address` });
    const sections = manifest.settings?.sections;
    if (!Array.isArray(sections) || !sections.length)
        return [...problems, { code: 'MALFORMED', detail: 'a settings page with no section draws nothing' }];
    const columns = new Map((manifest.columns ?? []).map((c) => [c.key, c]));
    const actions = new Map((manifest.actions ?? []).map((a) => [a.key, a]));
    const placed = new Map();
    const keys = new Set();
    for (const section of sections) {
        if (!section || typeof section.key !== 'string' || !Array.isArray(section.facts)) {
            problems.push({ code: 'MALFORMED', detail: 'a section has a key and its facts' });
            continue;
        }
        if (keys.has(section.key))
            problems.push({ code: 'MALFORMED', detail: `two sections are called "${section.key}"` });
        keys.add(section.key);
        if (!section.facts.length)
            problems.push({ code: 'MALFORMED', detail: `section "${section.key}" shows nothing` });
        for (const fact of section.facts) {
            if (!columns.has(fact))
                problems.push({ code: 'MALFORMED', detail: `section "${section.key}" shows "${fact}", which is not a column of this screen` });
            else if (placed.has(fact))
                problems.push({ code: 'MALFORMED', detail: `"${fact}" is shown in "${placed.get(fact)}" and "${section.key}"; a value is said once` });
            else
                placed.set(fact, section.key);
        }
        if (section.action !== undefined) {
            const action = actions.get(section.action);
            if (!action)
                problems.push({ code: 'MALFORMED', detail: `section "${section.key}" is changed by "${section.action}", which is not an action of this screen` });
            else if (!(action.fields ?? []).length || action.bulk || action.lookup_only)
                problems.push({ code: 'MALFORMED', detail: `section "${section.key}" is changed by "${section.action}", which is not a form` });
        }
    }
    for (const c of manifest.columns ?? []) {
        if (!c.hidden && !placed.has(c.key))
            problems.push({ code: 'MALFORMED', detail: `"${c.key}" is in no section: place it, or mark it hidden` });
    }
    return problems;
}
function inspectReport(manifest) {
    const problems = [...inspectExport(manifest)];
    if (!manifest.source?.path)
        problems.push({ code: 'NO_SOURCE', detail: 'a report with no source has nothing to ask the server for' });
    if (!(manifest.columns ?? []).length)
        problems.push({ code: 'MALFORMED', detail: 'a report draws at least one column beside its lines' });
    for (const column of manifest.columns ?? []) {
        if (!REPORT_COLUMN_TYPES.includes((column.type ?? 'text'))) {
            problems.push({ code: 'UNSUPPORTED_COLUMN', column: column.key, detail: `a report's column is ${REPORT_COLUMN_TYPES.join(', ')}; "${column.type}" is not one` });
        }
    }
    const params = manifest.report?.params;
    if (params !== undefined && !Array.isArray(params))
        return [...problems, { code: 'MALFORMED', detail: 'report.params is not a list of parameters' }];
    for (const p of params ?? []) {
        if (!p || typeof p.key !== 'string' || !REPORT_PARAM_TYPES.includes(p.type)) {
            problems.push({ code: 'UNSUPPORTED_FIELD', field: String(p?.key), detail: `parameter type "${String(p?.type)}" has no control in this version` });
        }
    }
    const compare = manifest.report?.compare;
    if (compare && !(params ?? []).some((p) => p.key === compare.param)) {
        problems.push({ code: 'MALFORMED', detail: `the comparison is turned on by "${compare.param}", which is not a parameter of this report` });
    }
    problems.push(...inspectReportChart(manifest));
    return problems;
}
/** A report's chart draws only what this version can: a known draw, an axis it can name, figure columns it was given. */
function inspectReportChart(manifest) {
    const chart = manifest.report?.chart;
    if (chart === undefined)
        return [];
    if (!chart || typeof chart !== 'object' || !REPORT_CHART_DRAWS.includes(chart.draw)) {
        return [{ code: 'MALFORMED', detail: `a report's chart drawn as "${String(chart?.draw)}" is not one this version draws` }];
    }
    if (chart.tone !== undefined && !TONES.includes(chart.tone))
        return [{ code: 'MALFORMED', detail: `the chart's tone "${String(chart.tone)}" is not one of the six` }];
    const columns = manifest.columns ?? [];
    if (chart.by !== 'label' && !columns.some((c) => c.key === chart.by && (c.type ?? 'text') === 'text')) {
        return [{ code: 'MALFORMED', detail: `the chart names its rows by "${String(chart.by)}", which is neither "label" nor a text column of this report` }];
    }
    const keys = reportChartSeries(manifest);
    if (keys === null)
        return [{ code: 'MALFORMED', detail: 'the chart\'s series are "columns" or a list of figure columns of this report' }];
    if (!keys.length)
        return [{ code: 'MALFORMED', detail: 'the chart has no figure column to draw' }];
    if (new Set(keys).size !== keys.length)
        return [{ code: 'MALFORMED', detail: 'the chart names one series twice' }];
    if (keys.length > MAX_CHART_SERIES)
        return [{ code: 'MALFORMED', detail: `the chart draws ${keys.length} series; at most ${MAX_CHART_SERIES} are read` }];
    return [];
}
/** The most series a report's chart draws (chart.tsx `MAX_SERIES`): past four, nobody reads them. */
export const MAX_CHART_SERIES = 4;
/**
 * The columns a report's chart draws, in order: every declared figure column for `columns`, else
 * the keys named — null when one named is not a figure column of the report.
 */
export function reportChartSeries(manifest) {
    const chart = manifest.report?.chart;
    if (!chart)
        return [];
    const figures = (manifest.columns ?? []).filter((c) => (c.type ?? 'text') !== 'text').map((c) => c.key);
    if (chart.series === 'columns')
        return figures;
    if (!Array.isArray(chart.series) || chart.series.some((k) => typeof k !== 'string' || !figures.includes(k)))
        return null;
    return [...chart.series];
}
/** A hub arrives over the wire like any manifest: its sections are checked before anything is drawn. */
function inspectHub(manifest) {
    const hub = manifest.hub;
    if (!hub || !Array.isArray(hub.sections))
        return [{ code: 'MALFORMED', detail: 'a hub with no sections has nothing to draw' }];
    if (hub.component !== undefined && (typeof hub.component !== 'string' || !/^[a-z][a-z0-9_]*\.[A-Z][A-Za-z0-9]*$/.test(hub.component))) {
        return [{ code: 'MALFORMED', detail: `a hub's component is named module.Component, and "${String(hub.component)}" is not` }];
    }
    // A hub its unit draws may have no section of its own; any other draws only its sections.
    if (!hub.sections.length && !hub.component)
        return [{ code: 'MALFORMED', detail: 'a hub with no sections has nothing to draw' }];
    const keys = new Set();
    for (const section of hub.sections) {
        if (!section || typeof section.key !== 'string' || !HUB_DRAWS.includes(section.draw)) {
            return [{ code: 'MALFORMED', detail: `a hub section drawn as "${String(section?.draw)}" is not one this version draws` }];
        }
        if (keys.has(section.key))
            return [{ code: 'MALFORMED', detail: `two hub sections are called "${section.key}"` }];
        keys.add(section.key);
    }
    for (const scope of hub.scope ?? []) {
        if (!scope.values?.length && !scope.options_from)
            return [{ code: 'MALFORMED', detail: `the scope "${String(scope.key)}" has no values and says where to read none` }];
        if (scope.options_from && !SOURCE_PATH.test(scope.options_from.path))
            return [{ code: 'BAD_SOURCE_PATH', detail: `the scope "${scope.key}" reads its values from "${scope.options_from.path}", which is not a path` }];
        // A hub has no form and no row: a `{name}` in a scope's route would never be filled.
        if (scope.options_from && namedIn(scope.options_from.path).length)
            return [{ code: 'MALFORMED', detail: `the scope "${scope.key}" reads its values from "${scope.options_from.path}", which names a value a hub has nowhere to read` }];
    }
    for (const section of hub.sections) {
        if ((section.draw === 'buckets' || section.draw === 'timeline') && !hub.sections.some((s) => s.key === section.of && s.draw === 'feed')) {
            return [{ code: 'MALFORMED', detail: `"${section.key}" counts the feed "${String(section.of)}", which this hub does not have` }];
        }
    }
    // The hub's own actions are drawn in its head, about the page: there is no row to name, nor one to
    // weigh a `when` on beyond the columns the hub itself declares.
    const columns = new Set((manifest.columns ?? []).map((c) => c.key));
    for (const action of manifest.actions ?? []) {
        if (action.row_key !== undefined || isRowAction(action))
            return [{ code: 'MALFORMED', detail: `the hub's action "${action.key}" names a row; a hub has none` }];
        const missing = (action.when === undefined ? [] : [action.when].flat()).find((c) => !columns.has(c.key));
        if (missing)
            return [{ code: 'MALFORMED', detail: `the hub's action "${action.key}" is offered by "${missing.key}", which is not a column of this hub` }];
    }
    return [];
}
export function factKey(fact) {
    return typeof fact === 'string' ? fact : fact.key;
}
export function subtitleKey(item) {
    return typeof item === 'string' ? item : item.key;
}
/** `{id}` in a path, filled with a record's id — refused, like a row action's placeholder, when the id is not a name. */
export function withId(template, id) {
    if (!NAME.test(id))
        return null;
    return template.replace(/\{id\}/g, encodeURIComponent(id));
}
/** Where a record is read: its source path with the id put in, or null when the id cannot be. */
export function recordPath(manifest, id) {
    const path = manifest.source?.path;
    return path ? withId(path, id) : null;
}
/** Where a card asks: its path and its declared query, each `{id}` the id of the record it sits on. */
export function cardRequest(manifest, id) {
    const path = manifest.source?.path ? withId(manifest.source.path, id) : null;
    if (!path)
        return null;
    if (!NAME.test(id))
        return null;
    // `{id}` anywhere in a value: a bare id, or an owner reference built around it (`parties.party:{id}`).
    const pairs = Object.entries(manifest.source?.filter ?? {}).map(([k, v]) => {
        const value = v.replace(/\{id\}/g, id);
        return `${encodeURIComponent(k)}=${encodeURIComponent(value)}`;
    });
    return pairs.length ? `${path}?${pairs.join('&')}` : path;
}
/**
 * The entity a list's rows are: its module and its `entity`, when the module declares that entity.
 * The caller answers whether it is declared (and openable); this only names the key.
 */
export function listEntity(manifest) {
    const dot = manifest.screen?.indexOf('.') ?? -1;
    if (dot < 1 || !manifest.entity || !/^[a-z][a-z0-9_]*$/.test(manifest.entity))
        return null;
    return `${manifest.screen.slice(0, dot)}.${manifest.entity}`;
}
export function fieldsOf(manifest) {
    return [...(manifest.fields ?? []), ...(manifest.actions ?? []).flatMap((a) => a.fields ?? [])];
}
/**
 * Turns a settings group's JSON Schema into fields. The schema is the platform's statement of what
 * a setting is; deriving the control from it means a new setting needs no screen change, and a
 * type nobody has drawn a control for is reported rather than rendered as a text box.
 */
export function fieldsFromSchema(schema, labels = {}) {
    const properties = (schema['properties'] ?? {});
    const required = Array.isArray(schema['required']) ? schema['required'] : [];
    return Object.entries(properties).map(([key, raw]) => {
        // JSON Schema allows a property schema of true, false or anything else. Reading keys off a
        // null throws, and off a boolean finds Object.prototype's own members as if they were a title.
        const spec = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
        const enumerated = choices(spec['enum']);
        const declared = typeof spec['type'] === 'string' ? spec['type'] : undefined;
        // An enum this version cannot offer faithfully is a refusal, not a free text box. Falling back
        // to the declared type would let a person type anything into a setting whose whole point is
        // that it is one of three things.
        const unusable = Array.isArray(spec['enum']) && !enumerated;
        const type = unusable ? undefined
            : enumerated ? 'select'
                : declared === 'boolean' ? 'boolean'
                    : declared === 'integer' ? 'integer'
                        : declared === 'number' ? 'number'
                            : declared === 'string' ? 'text'
                                : undefined;
        const field = {
            key,
            label: labels[key] ?? (typeof spec['title'] === 'string' ? spec['title'] : key),
            ...(type ? { type } : {}),
            ...(required.includes(key) ? { required: true } : {}),
            ...(enumerated ? { options: enumerated.map((v) => ({ value: v, label: String(v) })) } : {}),
            ...(typeof spec['description'] === 'string' ? { help: spec['description'] } : {}),
        };
        return field;
    });
}
/**
 * The members of an enum this version can offer as a choice, or nothing at all. A control names a
 * choice with a label, and two members that write themselves the same way — 1 and "1", or any two
 * objects — cannot be told apart once chosen. Refusing the field is the honest answer; picking the
 * first match would send a value the person did not choose, and the schema would accept it.
 */
function choices(raw) {
    if (!Array.isArray(raw) || !raw.length)
        return undefined;
    const seen = new Set();
    for (const v of raw) {
        if (v !== null && typeof v !== 'string' && typeof v !== 'number' && typeof v !== 'boolean')
            return undefined;
        // Two members that write themselves the same way are two labels a person cannot tell apart.
        if (seen.has(String(v)))
            return undefined;
        seen.add(String(v));
    }
    return raw;
}
/**
 * The rows, out of whatever the endpoint answered with.
 *
 * Refusing here rather than falling back to an empty list is the whole point: an empty table and a
 * misread response look identical to a person, and only one of them is true. So when the rows are
 * not where the manifest says, the answer names what the document actually holds — that is what
 * turns a wrong `rows` from a screen that shows nothing into a sentence saying which key to use.
 */
export function rowsOf(body, manifest) {
    // A query source answers the list standard's `{rows,total,totals}` (§9) whether or not the
    // manifest said where the rows are; the routes that also keep an older array answer for readers
    // that ask no question still answer an array, read as before.
    const wrapped = Boolean(manifest.source?.query) && Boolean(body) && typeof body === 'object' && !Array.isArray(body);
    const where = manifest.source?.rows ?? (wrapped ? 'rows' : undefined);
    let value = body;
    if (where) {
        if (!body || typeof body !== 'object' || Array.isArray(body)) {
            return { ok: false, detail: `expected an object with "${where}" in it, and the answer is ${describe(body)}` };
        }
        value = body[where];
        if (value === undefined) {
            const keys = Object.keys(body);
            return { ok: false, detail: `no "${where}" in the answer; it has ${keys.length ? keys.map((k) => `"${k}"`).join(', ') : 'no properties at all'}` };
        }
    }
    if (!Array.isArray(value)) {
        return { ok: false, detail: where ? `"${where}" is ${describe(value)}, not a list of rows` : `the answer is ${describe(value)}, not a list of rows` };
    }
    const bad = value.findIndex((r) => !r || typeof r !== 'object' || Array.isArray(r));
    if (bad >= 0)
        return { ok: false, detail: `row ${bad + 1} is ${describe(value[bad])}, and a row has to be a record` };
    const result = { ok: true, rows: value };
    if (where) {
        const wrapper = body;
        if (typeof wrapper['total'] === 'number')
            result.total = wrapper['total'];
        if (wrapper['totals'] && typeof wrapper['totals'] === 'object')
            result.totals = wrapper['totals'];
    }
    return result;
}
function describe(value) {
    if (value === null)
        return 'null';
    if (Array.isArray(value))
        return 'a list';
    return `a ${typeof value}`;
}
/**
 * Whether this version can carry the action out.
 *
 * An action that fails this is drawn disabled rather than hidden: somebody holding the permission
 * should see that the thing exists and is not ready, instead of wondering whether it was meant to
 * be there. And it is decided per action — one button with nowhere to send would otherwise take
 * the whole screen down with it.
 *
 * The path is checked here and not only at build time because the manifest arrives over the wire:
 * the request carries the viewer's token, so the manifest may choose which path on an already
 * trusted origin is asked, and may not choose anything else.
 */
export function runnable(action) {
    // Held back by its pre-open condition (resolveAvailable): not run, wherever it is drawn.
    if (heldBack(action))
        return false;
    if (action.component || action.screen)
        return true;
    const request = action.request;
    if (!request || !ACTION_METHODS.includes(request.method))
        return false;
    if (!SOURCE_PATH.test(request.path))
        return false;
    // A placeholder has nothing to fill it on a screen-level action: there is no row.
    if (action.scope !== 'row' && PLACEHOLDER.test(request.path)) {
        PLACEHOLDER.lastIndex = 0;
        return false;
    }
    PLACEHOLDER.lastIndex = 0;
    // Revealing nothing is not revealing. An action that says the answer matters and then names
    // nothing in it would post, close, and lose whatever the person was meant to keep.
    if (action.then === 'reveal' && !action.reveal?.length)
        return false;
    // A follow shows its outcome in the reveal, so there must be one; and its box must be a box.
    if (action.follow) {
        if (action.then !== 'reveal')
            return false;
        const box = action.follow.when ? action.fields?.find((f) => f.key === action.follow.when) : undefined;
        if (action.follow.when && (!box || box.type !== 'boolean'))
            return false;
    }
    if (action.then !== undefined && !THEN_VALUES.includes(action.then))
        return false;
    return true;
}
/**
 * Is this field drawn, given what the form holds now? A field is drawn unless its `when` fails.
 * One answer for the controls, the required check and the body, so a field hidden by a choice is
 * never both invisible and required, and what was typed into it before the choice changed is not
 * sent behind the person's back.
 */
export function fieldShown(field, values) {
    return holds(values, field.when);
}
/** The fields drawn right now, in their declared order. */
export function shownFields(action, values) {
    return (action.fields ?? []).filter((f) => fieldShown(f, values));
}
/**
 * The choices this viewer is offered: those naming no permission, and those whose permission they
 * hold. The field unchanged when nothing is taken away, so a caller comparing identity sees none.
 */
export function offeredField(field, may) {
    if (!field.options?.some((o) => o.permission))
        return field;
    return { ...field, options: field.options.filter((o) => !o.permission || may(o.permission)) };
}
/**
 * A form's parts: each declared section with its fields (the ones drawn now — those it lists, in its
 * order, then those naming it with `section`), and one untitled part for any field no section takes:
 * first when the form opens with such a field, last otherwise. A section a choice empties is dropped.
 */
export function sectionsOf(action, values) {
    const shown = shownFields(action, values);
    const byKey = new Map(shown.map((f) => [f.key, f]));
    const placed = new Set();
    const declared = action.sections ?? [];
    const known = new Set(declared.map((s) => s.key));
    for (const section of declared)
        for (const k of section.fields ?? [])
            placed.add(k);
    const parts = [];
    const taken = new Set();
    for (const section of declared) {
        const listed = (section.fields ?? []).map((k) => byKey.get(k)).filter((f) => Boolean(f) && !taken.has(f.key));
        const named = shown.filter((f) => f.section === section.key && !placed.has(f.key) && !taken.has(f.key));
        const fields = [...listed, ...named];
        for (const f of fields)
            taken.add(f.key);
        if (fields.length) {
            parts.push({
                key: section.key, label: section.label ?? '', fields,
                ...(section.label_key ? { label_key: section.label_key } : {}),
                ...(section.description ? { description: section.description } : {}),
                ...(section.description_key ? { description_key: section.description_key } : {}),
            });
        }
    }
    const rest = shown.filter((f) => !taken.has(f.key) && !(f.section && known.has(f.section) && !placed.has(f.key)));
    if (!rest.length)
        return parts;
    const part = { key: '', label: '', fields: rest };
    return shown[0] && rest[0] === shown[0] ? [part, ...parts] : [...parts, part];
}
/** The fields a person has not filled in, of those the action says it needs. */
export function missingRequired(action, values) {
    // A field that is not shown cannot be filled in, so it is not asked for.
    return visibleFields(action, values).filter((f) => {
        if (!f.required)
            return false;
        // Not drawn, not asked for: a field the choice above it hid cannot hold the form back.
        if (!fieldShown(f, values))
            return false;
        const v = values[f.key];
        if (f.type === 'audio')
            return !isMediaRef(v);
        // Lines left empty at the end are not lines: a required field with none kept is not filled in.
        if (isLinesField(f))
            return keptLines(f, v).length === 0;
        if (f.type === 'file') {
            const held = (one) => {
                if (!one || typeof one !== 'object' || Array.isArray(one))
                    return false;
                if (f.store === 'media')
                    return isMediaRef(one);
                const file = one;
                return Boolean(file.name && file.content_base64);
            };
            if (f.multiple)
                return !Array.isArray(v) || v.length === 0 || !v.every(held);
            return !held(v);
        }
        return v === undefined || v === null || v === '';
    });
}
/**
 * Starting values for an action form: declared samples, then the row when this is a row action.
 * The row wins so an edit does not overwrite live data with the sample.
 */
export function initialValues(action, row) {
    const seed = {};
    for (const field of action.fields ?? []) {
        // Never prefilled: not from a sample, not from the row, whatever either holds.
        if (isSecret(field))
            continue;
        if (field.value !== undefined)
            seed[field.key] = field.value === '$today' ? today() : field.value;
        // Usually the row's property of the same name. Not always: a payment's `amount` is its total,
        // and allocating it starts from what is still open; a copy's `key` must not start as the
        // original's, or saving it untouched is refused as taken.
        const source = field.from_row === false ? null : field.from_row ?? field.key;
        if (row && source !== null && row[source] !== undefined)
            seed[field.key] = row[source];
        // Lines open as the row's, then blank lines up to the fewest the field takes.
        if (isLinesField(field))
            seed[field.key] = startLines(field, seed[field.key]);
    }
    return seed;
}
/** What is sent: the form's values under the field keys, and nothing the form did not collect. */
/**
 * The action as its form's answers send it: for a `sent_by` form, the request, reveal and follow
 * of the way chosen — or the action as declared while nothing is chosen, or for any other form.
 */
export function sentAs(action, values) {
    const by = action.sent_by;
    const way = by ? by.as[String(values[by.field] ?? '')] : undefined;
    if (!by || !way)
        return action;
    const { follow: _f, reveal: _r, ...rest } = action;
    return { ...rest, request: way.request, ...(way.reveal ? { reveal: way.reveal } : {}), ...(way.follow ? { follow: way.follow } : {}) };
}
/**
 * What a `send: "changes"` form changed: the choices its list field gained and lost against the
 * value it opened with (the row's). Null for any other form, or one without a list field.
 */
export function listChanges(action, values, row) {
    if (action.send !== 'changes')
        return null;
    const field = (action.fields ?? []).find((f) => f.type === 'multiselect');
    if (!field)
        return null;
    const source = field.from_row === false ? null : field.from_row ?? field.key;
    const list = (v) => (Array.isArray(v) ? v.map((x) => String(x)) : []);
    const was = new Set(list(row && source !== null ? row[source] : undefined));
    const now = new Set(list(values[field.key]));
    return { add: [...now].filter((x) => !was.has(x)), remove: [...was].filter((x) => !now.has(x)) };
}
export function actionBody(action, values, row) {
    // Only what changed, so two people editing one list at once both keep theirs: the route applies
    // `add` and `remove` to what the list holds when it writes, not to what this form was shown.
    const changes = listChanges(action, values, row);
    const chooser = action.sent_by?.field;
    if (chooser) {
        const { [chooser]: _way, ...rest } = values;
        return actionBody({ ...action, sent_by: undefined, fields: (action.fields ?? []).filter((f) => f.key !== chooser) }, rest, row);
    }
    if (changes)
        return { ...(changes.add.length ? { add: changes.add } : {}), ...(changes.remove.length ? { remove: changes.remove } : {}) };
    const body = {};
    // A row action carries the version the row was read at, so a route that checks it refuses a
    // second press or a decision on a row somebody else changed meanwhile (#29). A form with a field
    // of that name says its own; a route that does not read it loses nothing.
    if (action.scope === 'row' && row && Number.isInteger(row['version']) && !(action.fields ?? []).some((f) => f.key === 'version')) {
        body['version'] = row['version'];
    }
    // A field hidden by its `when` is not the person's answer. On a new record it is left out. On an
    // existing one it is sent as null — "does not apply" — because leaving it out means "unchanged" to
    // an edit, and an end date kept after the contract became open-ended is a contract the server
    // refuses, through a field the person can no longer see to correct.
    if (action.scope === 'row') {
        for (const field of action.fields ?? []) {
            if (field.when && !holds(values, field.when) && action.follow?.when !== field.key)
                body[field.key] = null;
        }
    }
    for (const field of visibleFields(action, values)) {
        // The follow's box is a choice about the second request, not part of the first.
        if (action.follow?.when === field.key)
            continue;
        // A field the form is not drawing is not sent: an address typed and then set aside by choosing
        // "an existing user" instead is not an invitation the person still meant.
        if (!fieldShown(field, values))
            continue;
        // A secret left empty is "keep the one there is", which is saying nothing about it.
        if (isSecret(field) && (values[field.key] === undefined || values[field.key] === null || values[field.key] === ''))
            continue;
        // Lines send their kept lines' editable cells, and each line's `id` when the route sent one.
        if (isLinesField(field)) {
            body[field.key] = sentLines(field, values[field.key]);
            continue;
        }
        if (values[field.key] !== undefined)
            body[field.key] = values[field.key];
    }
    return body;
}
/** Whether this action belongs on each row rather than above the list. */
export function isRowAction(action) {
    return action.scope === 'row';
}
/**
 * Does this row satisfy every condition?
 *
 * One implementation, and it lives here because two of them is how `equals_any` came to work for
 * the rows a view keeps and do nothing for the actions a row is drawn with. An action whose `when`
 * matches no row is invisible, and an invisible action looks exactly like one the person is not
 * allowed to run — so the bug reads as a permission and nobody reports it.
 */
export function holds(row, where) {
    if (!where)
        return true;
    // The comparison stays raw — no coercion, so "1" and 1 are two values and a screen cannot match
    // a number by writing its digits.
    return (Array.isArray(where) ? where : [where]).every((c) => c.not_equals_any ? !c.not_equals_any.some((v) => row[c.key] === v)
        : c.equals_any ? c.equals_any.some((v) => row[c.key] === v) : row[c.key] === c.equals);
}
/** Whether a row is one this action is drawn on. */
/**
 * The action a tap on a row runs when the row's entity has no record page (L-19): the row's first
 * action that only goes somewhere — a `screen`, no fields to fill, not destructive — that holds for
 * this row and is the viewer's. A touch screen has no hover to find «⋯» by, and a payslip that
 * opened only from there took three taps (EMP-18). Undefined: the row opens nothing on a tap.
 */
export function rowOpenAction(manifest, row, holds) {
    return (manifest.actions ?? []).find((a) => isRowAction(a) && Boolean(a.screen) && !a.fields?.length && !a.destructive && !a.component
        && (!a.permission || holds(a.permission)) && appliesTo(a, row));
}
export function appliesTo(action, row) {
    return holds(row, action.when);
}
/**
 * A path template with each `{name}` filled — the one rule `fields_from` and `options_from` share.
 * The form's value of that field when the form has one (it wins over the row), else the row's
 * property; null while any of them is empty, or holds something that is not a name (the same rule
 * `pathFor` keeps: `..` survives escaping). A template with no `{name}` is returned as it is.
 */
export function fillNamed(template, named) {
    const own = new Set(named?.fields ?? []);
    let refused = false;
    const path = template.replace(PLACEHOLDER, (_whole, key) => {
        const value = own.has(key) ? named?.values[key] : named?.row?.[key];
        if (value === null || value === undefined || value === '' || typeof value === 'object') {
            refused = true;
            return '';
        }
        const text = String(value);
        if (!NAME.test(text)) {
            refused = true;
            return '';
        }
        return encodeURIComponent(text);
    });
    return refused ? null : path;
}
/**
 * The shape an `options_from.path` may take: a path on the unit whose segments may be `{name}`, and
 * an optional query of `key=value` pairs whose values are a name or a `{name}`. The schema says the
 * same (`screen.schema.json`); this is the check where the manifest may not be the one checked.
 */
const OPTIONS_PATH = /^\/(?:(?:[^\s?#{}/]+|\{[a-z][a-z0-9_]*\})(?:\/(?:[^\s?#{}/]+|\{[a-z][a-z0-9_]*\}))*\/?)?(?:\?[a-z][a-z0-9_]*=(?:[A-Za-z0-9_.-]+|\{[a-z][a-z0-9_]*\})(?:&[a-z][a-z0-9_]*=(?:[A-Za-z0-9_.-]+|\{[a-z][a-z0-9_]*\}))*)?$/;
/** The `{name}`s a path names, in order. */
export function namedIn(path) {
    return [...path.matchAll(PLACEHOLDER)].map((m) => m[1]);
}
/**
 * The path an `options_from` asks the unit for, now.
 *
 * A named `source` is a closed catalogue with a default path; an explicit `path` always wins, and
 * each `{name}` in it is filled from the form's values or the row (`fillNamed`). Null while a named
 * value is empty — the field offers nothing and nothing is asked — and for a path that is not one,
 * rather than guessing: ActionDialog never fetches somewhere nobody declared. Without `named`, a
 * path that names a value has nowhere to read it from, and is null too.
 */
export function optionsFromPath(from, named) {
    if (from.path)
        return OPTIONS_PATH.test(from.path) ? fillNamed(from.path, named) : null;
    if (from.source && OPTIONS_SOURCES.includes(from.source)) {
        return OPTIONS_SOURCE_PATHS[from.source];
    }
    return null;
}
/**
 * Where each field of a form with `options_from` reads its choices now, by field key: what the
 * shell compares to what it last asked, so a changed value asks again for the fields that name it
 * and for no other. A row is read only on a row action, as `fields_from` reads it.
 */
export function optionsPaths(action, values, row) {
    const fields = (action.fields ?? []).map((f) => f.key);
    const named = { fields, values, ...(action.scope === 'row' && row ? { row } : {}) };
    const out = {};
    for (const f of action.fields ?? []) {
        if (f.options_from)
            out[f.key] = optionsFromPath(f.options_from, named);
        // A line column's choices are the same for every line: read once, by the head fields alone.
        if (isLinesField(f))
            for (const c of lineColumns(f))
                if (c.options_from)
                    out[lineChoiceKey(f.key, c.key)] = optionsFromPath(c.options_from, named);
    }
    return out;
}
/**
 * The fields whose choices must be asked again: each whose path now differs from the one it was
 * last asked at (or that was never asked). A value that changes asks again for the fields that name
 * it, and for no other; a path that became null is listed too — its field now offers nothing.
 */
export function changedPaths(asked, now) {
    return Object.entries(now).filter(([key, path]) => !(key in asked) || asked[key] !== path);
}
/**
 * A route's answer as a field's choices. A row with no value is not a choice: `/members` lists
 * invitations beside members, and an invitation has no membership yet — drawn, it was an option
 * called "null" that the server refused. Null when the answer is not a list where `rows` says.
 */
export function optionsOfAnswer(from, body, path, locale, words) {
    const rows = rowsOf(body, { screen: 'x', kind: 'list', source: { path, ...(from.rows ? { rows: from.rows } : {}) } });
    if (!rows.ok)
        return null;
    return rows.rows.filter((o) => o[from.value] !== null && o[from.value] !== undefined).map((o) => ({
        value: String(o[from.value]),
        // Words the field builds from the row (`label_template`), in the reader's language, come first;
        // a row that also carries `<label>_ar` is named in Arabic on an Arabic screen: roles do.
        label: words?.(o) || String((locale === 'ar' && o[`${from.label}_ar`]) || (o[from.label] ?? o[from.value])),
        // A row that names its words by catalogue key (`label_key_from`, else `{label}_key`) is worded
        // by the catalogue, the label above its fallback: a fact a module declared in English.
        ...(words?.(o) ? {} : labelKeyOf(o[from.label_key_from ?? `${from.label}_key`])),
        // A second line that only repeats the first says nothing: a user with no name is labelled by
        // their address, and drawing the address again under it read as a mistake.
        ...(from.group && o[from.group] !== undefined && o[from.group] !== null && String(o[from.group]) !== String(o[from.label])
            ? { group: String(o[from.group]) } : {}),
        ...(from.row && o[from.row] !== undefined && o[from.row] !== null ? { row: String(o[from.row]) } : {}),
        ...(from.column && o[from.column] !== undefined && o[from.column] !== null ? { column: String(o[from.column]) } : {}),
        ...(from.carry ? { carry: Object.fromEntries(from.carry.map((k) => [k, o[k]])) } : {}),
    }));
}
/** A row's catalogue key for an option's words, when it is one: a dotted string. */
function labelKeyOf(v) {
    return typeof v === 'string' && v.includes('.') ? { label_key: v } : {};
}
/**
 * Whether a field's value is still one of its choices. Empty is kept (there is nothing to clear); a
 * list of several (`multiselect`) keeps only what is still offered — answered as the value to keep.
 */
export function keptChoice(value, options) {
    if (value === null || value === undefined || value === '')
        return value;
    const offered = (v) => (options ?? []).some((o) => Object.is(o.value, v) || String(o.value) === String(v));
    if (Array.isArray(value))
        return value.filter(offered);
    return offered(value) ? value : undefined;
}
/**
 * The path for this action against this row, or null when the row cannot supply one.
 *
 * Null rather than a best effort: a missing property would otherwise become the literal text
 * "undefined" in a URL, and a value that is not a name would become a path of somebody else's
 * choosing. Either way the action is not drawn, which is the only honest answer.
 */
export function pathFor(action, row) {
    const template = action.request?.path;
    if (!template)
        return null;
    if (!isRowAction(action))
        return template;
    if (!row)
        return null;
    let refused = false;
    const path = template.replace(PLACEHOLDER, (_whole, key) => {
        const value = row[key];
        if (value === null || value === undefined) {
            refused = true;
            return '';
        }
        const text = String(value);
        if (!NAME.test(text)) {
            refused = true;
            return '';
        }
        return encodeURIComponent(text);
    });
    return refused ? null : path;
}
// ── advice in a form preview's answer ──────────────────────────────────────────────────────
/**
 * A route's advice as the lines a form draws: `{ advice: [...] }` or the list itself, each line a
 * `message` in words (and the catalogue `key` that says it in the reader's language, with its
 * `vars`), in a tone of the design system's — anything else is `neutral`. A line with no words is
 * dropped, and at most `MAX_ADVICE` are drawn. An answer that is not advice is no advice.
 */
export function adviceOfAnswer(body) {
    const list = Array.isArray(body) ? body : body && typeof body === 'object' ? body.advice : null;
    if (!Array.isArray(list))
        return [];
    const out = [];
    for (const item of list) {
        if (!item || typeof item !== 'object')
            continue;
        const o = item;
        if (typeof o.message !== 'string' || !o.message.trim())
            continue;
        const vars = {};
        if (o.vars && typeof o.vars === 'object' && !Array.isArray(o.vars)) {
            for (const [k, v] of Object.entries(o.vars))
                if (typeof v === 'string' || typeof v === 'number')
                    vars[k] = v;
        }
        out.push({
            tone: TONES.includes(o.tone) ? o.tone : 'neutral',
            ...(typeof o.key === 'string' && o.key ? { key: o.key } : {}),
            message: o.message, vars,
        });
        if (out.length === MAX_ADVICE)
            break;
    }
    return out;
}
// ── fields asked of a route (fields_from) ──────────────────────────────────────────────────────
/**
 * Where a form's further fields are asked now: `fields_from.path` with each `{name}` filled from the
 * form's value of that field — or, on a row action, the row's property when the form has no such
 * field — or null while one of them has no value yet, or holds something that is not a name (the
 * same rule `pathFor` keeps). Null means nothing is asked, and the form holds only what it declares.
 */
export function askedFieldsPath(action, values, row) {
    const from = action.fields_from;
    if (!from || typeof from.path !== 'string')
        return null;
    const fields = (action.fields ?? []).map((f) => f.key);
    const path = fillNamed(from.path, { fields, values, ...(action.scope === 'row' && row ? { row } : {}) });
    return path === null || !SOURCE_PATH.test(path) ? null : path;
}
/**
 * What a field a route answers may say: the shape of a declared field, less what a route has no
 * business deciding — where its choices come from (`options_from`: a second fetch the manifest did
 * not name), its section, its condition, where it starts from on a row. Anything else is refused.
 */
export const ASKED_FIELD_PROPERTIES = ['key', 'label', 'label_key', 'type', 'required', 'value', 'help', 'help_key', 'options', 'unit', 'decimals', 'min', 'max', 'format', 'min_length', 'max_length'];
/**
 * The form an action becomes once its route has said what else it asks (`fields_from`). A field the
 * route names that the form declares is completed, not replaced — the route may make it required and
 * say why, and its control, choices and section stay as declared; a new one is added after the
 * declared fields, drawn from what the route wrote. Refused, with the reason, rather than drawn
 * approximately: an answer that is not a list of fields, a field of a type this version has no control
 * for, a choice with nothing to choose from, a property a route may not set, more new fields than
 * `max` — which is the room the frame was chosen for.
 */
export function withAskedFields(action, answer) {
    const from = action.fields_from;
    if (!from)
        return { ok: true, action, added: 0 };
    const list = from.rows ? (answer && typeof answer === 'object' && !Array.isArray(answer) ? answer[from.rows] : undefined) : answer;
    if (!Array.isArray(list))
        return { ok: false, detail: from.rows ? `the answer has no list of fields under "${from.rows}"` : 'the answer is not a list of fields' };
    const declared = action.fields ?? [];
    const known = new Set(declared.map((f) => f.key));
    const completing = new Map();
    const added = [];
    const seen = new Set();
    for (const raw of list) {
        if (!raw || typeof raw !== 'object' || Array.isArray(raw))
            return { ok: false, detail: 'a field in the answer is not a field' };
        const f = raw;
        const key = f['key'];
        if (typeof key !== 'string' || !/^[a-z][a-z0-9_]*$/.test(key))
            return { ok: false, detail: `a field in the answer has no key, or a key that is not a name (${JSON.stringify(key)})` };
        if (seen.has(key))
            return { ok: false, detail: `the answer names "${key}" twice` };
        seen.add(key);
        const extra = Object.keys(f).filter((k) => !ASKED_FIELD_PROPERTIES.includes(k));
        if (extra.length)
            return { ok: false, detail: `field "${key}" says ${extra.map((k) => `"${k}"`).join(', ')}, which a route may not set` };
        if (f['required'] !== undefined && typeof f['required'] !== 'boolean')
            return { ok: false, detail: `field "${key}" says required is ${JSON.stringify(f['required'])}` };
        if (known.has(key)) {
            // Completed: what the route may add to a declared field is whether it is needed and why.
            completing.set(key, {
                ...(f['required'] !== undefined ? { required: f['required'] } : {}),
                ...(typeof f['help'] === 'string' ? { help: f['help'] } : {}),
                ...(typeof f['help_key'] === 'string' ? { help_key: f['help_key'] } : {}),
            });
            continue;
        }
        const type = f['type'];
        if (typeof type !== 'string' || !FIELD_TYPES.includes(type))
            return { ok: false, detail: `field "${key}" is of type ${JSON.stringify(type)}, which has no control in this version` };
        // The frame is decided before the route answers, and lines make a form a page.
        if (type === 'lines')
            return { ok: false, detail: `field "${key}" is lines, which a route may not add: a form with lines is a page from the start` };
        if (typeof f['label'] !== 'string' || !f['label'])
            return { ok: false, detail: `field "${key}" has no words to be asked with (label)` };
        const options = f['options'];
        if ((type === 'select' || type === 'multiselect') && (!Array.isArray(options) || !options.length))
            return { ok: false, detail: `field "${key}" is a choice with nothing to choose from` };
        if (options !== undefined && !Array.isArray(options))
            return { ok: false, detail: `field "${key}" has choices that are not a list` };
        for (const o of options ?? []) {
            const option = o;
            if (!option || typeof option !== 'object' || typeof option['label'] !== 'string' || !['string', 'number', 'boolean'].includes(typeof option['value'])) {
                return { ok: false, detail: `a choice of field "${key}" has no value or no words` };
            }
        }
        if (f['unit'] !== undefined && !FIELD_UNITS.includes(f['unit']))
            return { ok: false, detail: `field "${key}" counts in "${String(f['unit'])}", which this version does not word` };
        if (f['format'] !== undefined && !(String(f['format']) in FIELD_FORMATS))
            return { ok: false, detail: `field "${key}" has the shape "${String(f['format'])}", which this version does not check` };
        added.push(f);
    }
    if (added.length > from.max)
        return { ok: false, detail: `the answer adds ${added.length} fields, and this form has room for ${from.max}` };
    const fields = declared.map((f) => (completing.has(f.key) ? { ...f, ...completing.get(f.key) } : f));
    return { ok: true, action: { ...action, fields: [...fields, ...added] }, added: added.length };
}
export function movePlan(manifest, row, lane, may) {
    const board = manifest.board;
    if (!board?.move)
        return { kind: 'none', reason: 'unmovable' };
    if (String(row[board.by] ?? '') === String(lane))
        return { kind: 'none', reason: 'same' };
    const action = movable(manifest, row, may);
    if (!action)
        return { kind: 'none', reason: 'unmovable' };
    const values = { ...initialValues(action, row), [board.move.field ?? board.by]: lane };
    if (action.fields_from || missingRequired(action, values).length)
        return { kind: 'form', action, values };
    return { kind: 'run', action, values };
}
/** The action a row's card moves by, when this viewer may move this row at all: what makes a card draggable. */
export function movable(manifest, row, may) {
    const move = manifest.board?.move;
    if (!move)
        return null;
    const action = (manifest.actions ?? []).find((a) => a.key === move.action && isRowAction(a));
    if (!action || !runnable(action) || action.component || action.screen)
        return null;
    if (action.permission && !may(action.permission))
        return null;
    if (!appliesTo(action, row) || !pathFor(action, row))
        return null;
    return action;
}
// ── a record's steps from a route (record.stages.values_from) ───────────────────────────────────
/** Where a record's steps are read: `values_from.path` with each `{column}` the record's value, or null when one is missing. */
export function stagesPath(manifest, row) {
    const from = manifest.record?.stages?.values_from;
    if (!from || typeof from.path !== 'string')
        return null;
    let refused = false;
    const path = from.path.replace(PLACEHOLDER, (_whole, key) => {
        const value = row[key];
        if (value === null || value === undefined || value === '' || typeof value === 'object' || !NAME.test(String(value))) {
            refused = true;
            return '';
        }
        return encodeURIComponent(String(value));
    });
    return refused || !SOURCE_PATH.test(path) ? null : path;
}
/** More fields than this and a dialog lays them out two to a row. */
export const TWO_COLUMNS_AFTER = 5;
/** More fields than this and the form is not a dialog at all but a page of its own. */
export const PAGE_AFTER = 9;
/** Field types that take the whole row when a form has two columns. */
export const FULL_ROW_TYPES = ['textarea', 'multiselect', 'file', 'audio', 'keyvalue', 'lines'];
/**
 * How a declared form is framed — the rule every form in the system follows, in one place:
 * up to five fields a one-column dialog; six to nine a two-column dialog; more than nine, or an
 * action that says `presentation: "page"` because it would still scroll, a page of its own with two
 * columns. A dialog that scrolls is a form nobody sees the end of before pressing its button.
 *
 * Every declared field counts, including one drawn only on a condition (`when`): the frame is a
 * fact about the form, not about what has been chosen in it so far, so it never changes under the
 * person while they fill it in. A declared preview (`preview`) counts as one field: its facts sit
 * after the fields, and a form at the edge of a frame moves to the next rather than scroll.
 *
 * A form with a `lines` field is always a page: a grid of lines does not fit a dialog that never
 * scrolls, and a document with lines is a form people work in for a while.
 */
export function formLayout(action) {
    // The fields a route may add count as if they were there: the frame is chosen once, before the
    // route has said what the chosen step asks for, and it does not change when it answers. A preview
    // (`preview`) is a strip of facts after the fields, and counts as one more.
    const n = (action.fields?.length ?? 0) + (action.fields_from?.max ?? 0) + (action.preview && action.fields?.length ? 1 : 0);
    if (n > PAGE_AFTER || action.presentation === 'page' || (action.fields ?? []).some(isLinesField))
        return { frame: 'page', columns: 2 };
    return { frame: 'dialog', columns: n > TWO_COLUMNS_AFTER ? 2 : 1 };
}
/** Whether a field takes the whole row in a two-column form. */
export function fullRow(field) {
    return FULL_ROW_TYPES.includes(field.type ?? 'text');
}
/** The values a repeatable form starts the next entry with: its defaults, and what the last one marked sticky. */
export function nextEntry(action, last) {
    const next = initialValues(action);
    for (const f of action.fields ?? [])
        if (f.sticky && last[f.key] !== undefined)
            next[f.key] = last[f.key];
    return next;
}
/** Whether a form holds something the person typed that saving has not kept. */
export function unsaved(start, now) {
    const keys = new Set([...Object.keys(start), ...Object.keys(now)]);
    const empty = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
    for (const k of keys) {
        const a = start[k], b = now[k];
        if (empty(a) && empty(b))
            continue;
        if (JSON.stringify(a) !== JSON.stringify(b))
            return true;
    }
    return false;
}
/** The catalogue key of the refusal an end date earns for falling before its start, or null. */
export function orderProblem(field, values) {
    if (!field.after)
        return null;
    const end = values[field.key], start = values[field.after];
    if (typeof end !== 'string' || typeof start !== 'string' || !end || !start)
        return null;
    return end.slice(0, 10) < start.slice(0, 10) ? 'field.after' : null;
}
/** Days from one ISO day to another, both counted. Null when either is missing or they run backwards. */
export function spanDays(start, end) {
    if (typeof start !== 'string' || typeof end !== 'string' || !start || !end)
        return null;
    const a = Date.parse(start.slice(0, 10) + 'T00:00:00Z'), b = Date.parse(end.slice(0, 10) + 'T00:00:00Z');
    if (!Number.isFinite(a) || !Number.isFinite(b) || b < a)
        return null;
    return Math.round((b - a) / 86_400_000) + 1;
}
/** The values a chosen option carries, under `<field>.<property>` — what a `when` reads, never sent. */
export function carried(field, chosen, options) {
    const out = {};
    const keys = field.options_from?.carry ?? [];
    if (!keys.length)
        return out;
    const option = (options ?? []).find((o) => Object.is(o.value, chosen) || (chosen !== null && chosen !== undefined && String(o.value) === String(chosen)));
    for (const k of keys)
        out[`${field.key}.${k}`] = option?.carry?.[k];
    return out;
}
//# sourceMappingURL=manifest.js.map